/**
 * The UMED operations workspace (EP-004U phase 2). READ-ONLY.
 *
 * == Four destinations and one record ====================================
 *
 * MY WORK: the obligations this operator holds, through a lens — Needs DCF
 * account · Needs creation · Ready to submit · Exceptions · More.
 * FOLLOW-UPS: the dated approval checks, overdue first, because those are
 * the only obligations the substrate can date. APPROVED: cases where both
 * approval facts are in evidence, and nothing else. ALL CASES: the
 * authorized universe, for retrieval. Open a case and the context pane
 * answers, in this order: what is the next action, what is the evidence,
 * what did Finance do, what already happened.
 *
 * == The one sentence that must never be wrong ===========================
 *
 * A board word is not an approval. 2,564 open cases say APPROVED (or carry
 * a Finance word, which claims the same thing) and none of them can show a
 * NOCA or an FLMMIS. This screen never renders one of those as approved,
 * never counts one under Approved, and never offers a Finance handoff from
 * one. It says, in plain words, that the source claims it and the evidence
 * is incomplete — which is a statement about our records, not an accusation
 * about anybody's work.
 *
 * == No write controls ===================================================
 *
 * Not disabled ones — none. There is no mutation on the server to call, so
 * there is nothing here to grey out. A control that looked live would be a
 * claim about what eMerge owns that is not yet true.
 *
 * == Frontend visibility is never authorization ==========================
 *
 * The server decides every row and every field. What this file does with a
 * capability flag is choose whether to render a section, never whether the
 * data may be seen — it has already been withheld by then.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Why, firstReason } from '../../components/emerge/WithReason';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { updatedStamp, mondayRetiredLine, notCurrentText } from '../../components/workspaceFreshness';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import UmedResubmittalsBoard from './UmedResubmittalsBoard';
import UmedReplacementsBoard from './UmedReplacementsBoard';
import ReplacementRequestPanel from '../../components/replacements/ReplacementRequestPanel';
import { KIND_LABEL, KIND_NOT_STATED, dollars, termsLine } from '../finance/resubmittalsModel';

import FeedbackDrawer, { FeedbackButton } from '../../components/feedback/FeedbackDrawer';
import WorkflowPulse from '../../components/feedback/WorkflowPulse';
import { PromptSource } from '../../services/feedbackApi';
import {
  umedApi, personName, shortDate, shortDateTime, relativeDay, daysFromToday, stateLine, approvalLine, canDo,
  stageLabelOf, UMED_TYPE_LABEL, BLOCKER_LABEL, ACTION_LABEL, ATTENTION_LABEL, NOCA_LABEL, FLMMIS_LABEL,
  DCF_ACCOUNT_LABEL, HANDOFF_LABEL, LIFECYCLE_LABEL, EXCEPTION_LABEL,
  CREATION_REASON_LABEL, CORRECTION_TYPE_LABEL, DCF_NATIVE_LABEL, REQUIREMENT_LABEL, NOCA_RESULT_LABEL, FLMMIS_RESULT_LABEL, DECISION_LABEL,
  REVERSAL_REQUESTER_LABEL, reversalStateWords, FINANCE_STATE_LABEL, EXCEPTION_WORK_LABEL, SUBMISSION_METHOD_LABEL,
  financeLine, provenanceLine, parseTermsLine,
  PACKET_REASON_LABEL, PACKET_FIELD_LABEL, DOCUMENT_CLASS_LABEL, freshKey,
  Scope, Counts, LensKey, ExceptionKey, UmedCase, CaseDetail, OpenAction, Attention, Approval,
  Paged, WorkPage, FollowUpPage, ApprovedPage, ExceptionPage,
  Noca, NocaResult, FlmmisResult, CorrectionType, DcfAccountRecord, Correction, Reversal, SubmissionMethod,
  Packet, PacketInputs, PacketPreview, CaseDocument,
} from './umedApi';
import { freshKey as newActKey, UMED_UPLOAD_CLASSES, UMED_UPLOAD_LABEL, DCF_NEED_LABEL, isLensKey, purposeNeedsDcfAccount, exceptionShown, CORRECTION_NOTE_WITHHELD } from './umedApi';
import SendingChooser from './SendingChooser';
import CorrectedResend from './CorrectedResend';
import OriginalTermsForm from './OriginalTermsForm';
import NotThisResidents, { MisfiledNote, mdy } from '../documents/NotThisResidents';
import { isPlayable, useRecordingPlayer, RecordingAudio } from '../documents/RecordingPlayer';
import type { UmedUploadClass, UmedNote, DcfNeed, NocaEvidence, CaseFocus, DcfBenefits } from './umedApi';
// The approval email (2026-10-13): opened right after an approval, and from any approved case.
import ApprovalEmailPanel from './ApprovalEmailPanel';
import './umed.css';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import ReturnedToYou, { useReturned, returnedCount } from '../../components/sendBack/ReturnedToYou';
import { FindResidentEntry } from '../../components/residentDirectory';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import AtdPanel from '../../components/atd/AtdPanel';
import AtdResidentsBoard, { useAtdAllResidents } from '../../components/atd/AtdResidentsBoard';
import { residentsApi } from '../advocate/residentsApi';
import { FINANCIAL_RESPONSIBILITY_LABEL } from '../advocate/advocateApi';
import type { FinancialResponsibilityType } from '../advocate/advocateApi';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import BoardTable, { DispositionChip, ApproveAct, MondayDiffers, FLMMIS_EFFECT, nocaForward, CORRECTION_RESUB_OPEN_NOTE, correctionCompletedWords, todayLocal, OUTSIDE_NOTE_MAX, blockedWords,
  CorrectionResendPanel, CorrectionNotePanel, correctionResendOffered, correctionResendHeld, correctionHasNote, correctionResendLabel, CORRECTION_NOTE_LABEL, CORRECTION_NOTE_HAS_ONE,
  correctionResendEffect, CORRECTION_NOTE_EFFECT, DockStatus } from './BoardTable';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { UMED_STATUS } from '../../theme/mondayPalette';
import type { BoardTabKey } from './umedApi';
import { useHandoffSignal } from '../../contexts/NotificationsContext';
// UM-R5 (2026-10-02): the DCF mail tab and the case panel's DCF mail section.
import DcfMailView from './DcfMailView';
import DcfMailCaseSection from './DcfMailCaseSection';
import PacketPreparationPanel, { PreparePacketButton } from './PacketPreparationPanel';
import { CaseWaitingFaxes } from './WaitingFaxes';
// Gwynn, 2026-10-12: "(STUCK) Can't link Benefits" in the DCF account section (the board offers the same acts).
import DcfBenefitsLink, { stuckFactsOfRecord } from './DcfBenefitsLink';
import { PaidInFullChip } from './PaidInFullMark';
// 2026-10-13: the Board layout (operational Kanban) beside the table, and where an act chosen there lands in the case.
import UmedKanban from './UmedKanban';
import { boardLayoutOf, DEFAULT_BOARD_LAYOUT } from '../../components/board/kanban/layout';
// 2026-10-14: the views moved from the tab bar to the workspace sidebar (Isaiah Cotto).
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { BadgeCheck, CalendarClock, Ear, Files, Inbox, Mail, RotateCcw, SquareKanban, Table2 } from 'lucide-react';
import { useCaseAnchor } from './caseAnchor';
import { RecordSection, RecordSections } from '../../components/board/RecordSection';

/** Where a reconciliation starts: the board's own first tab. */
const DEFAULT_BOARD_TAB: BoardTabKey = 'umeds-needed';
/*
 * Every tab the server offers must be a key this list accepts, or choosing it
 * lands on the first view. `board-umeds-needed` (2026-09-24) had been missing,
 * so its tab silently showed UMEDs Needed; `problem-accounts` is UM-R1's.
 */
const BOARD_TAB_KEYS: readonly BoardTabKey[] = [
  'umeds-needed', 'board-umeds-needed', 'review-for-approvals', 'main-table', 'umeds-to-fax', 'umed-submitted',
  'dcf-submitted-flmmis', 'dcf-corrections', 'reversals', 'dcf-login-needed', 'new-approvals-noca', 'problem-accounts',
  // Josie Evans, second letter (2026-10-06).
  'dcf-correction-completed', 'sent-in-monday',
  // Gwynn, 2026-10-12: "(STUCK) Can't link Benefits".
  'dcf-benefits-stuck',
  // If-then processes 5 and 6 (2026-10-13): PL corrections / benefits lost, and re-vets due.
  'after-approval', 'revet-due',
  // APR 02 (2026-10-13): FLMMIS checks due.
  'flmmis-check-due',
  // Josie's Ready to Approve and Not qualifying / recert tabs, and the Monday-not-applied check. The server offers them;
  // without them here a click fell back to UMEDs Needed. backend/src/services/umed/__tests__/uiKeysMatchServer.test.js pins this list.
  'ready-to-approve', 'not-qualifying', 'monday-not-applied',
];
const isBoardTab = (v: string | null): v is BoardTabKey => v !== null && (BOARD_TAB_KEYS as readonly string[]).includes(v);

/**
 * The business-facing names (packet §5, §19). The route key `follow-ups`
 * stays stable underneath; the person sees Josie's operating unit by its
 * name.
 */
/**
 * EP-010 §9: `board` is the Monday-parity table — the ten operating views
 * Josie reconciles across, as one wide sortable table. It sits BESIDE the
 * operating views rather than replacing them: My work is what an operator
 * does today, Board is how the whole picture is checked.
 */
type View = 'work' | 'follow-ups' | 'approved' | 'all' | 'board' | 'dcf-mail' | 'atd' | 'resubmittals' | 'replacements';
/** `atd` (2026-10-13): "ATD — all residents", opened by the header's "Mark ATD for any resident" button rather than a tab. */
const VIEWS: readonly View[] = ['board', 'work', 'follow-ups', 'approved', 'all', 'dcf-mail', 'resubmittals', 'replacements', 'atd'];
/**
 * `follow-ups` is the DATED obligations — the ten-day NOCA/FLMMIS re-checks,
 * which somebody owns and which come due. It used to be labelled Review for
 * Approvals, and that was the collision behind the call of 2026-09-21: the
 * board's Review for Approvals is a statement about a CASE, this is a list of
 * one operator's dated work, and one label over two populations is what let
 * three people read three numbers and each be right. The board's question is
 * now the `review-for-approval` view, counted for everyone alike.
 */
const VIEW_LABEL: Record<View, string> = { work: 'My work', 'follow-ups': 'Approval checks', approved: 'Approved', all: 'All cases', board: 'Board', 'dcf-mail': 'DCF mail', resubmittals: 'Reversals Submitted', replacements: 'Replacements', atd: 'ATD — all residents' };
const BASE = '/portal/umed';
/**
 * The page's name: a view's label, and for the Board the layout too (verification F6, 2026-10-14), so the heading says
 * what the sidebar's Views group says -- "Board · Kanban" or "Board · Table". The ATD door's "Back to" uses the same.
 */
const titleOf = (v: View, layout: 'kanban' | 'table'): string => (v === 'board' ? `Board · ${layout === 'table' ? 'Table' : 'Kanban'}` : VIEW_LABEL[v]);

const isView = (v: string | null): v is View => v !== null && (VIEWS as readonly string[]).includes(v);
/**
 * The workspace opens on the Board, as the Kanban (Isaiah Cotto, 2026-10-14:
 * "The kanban views should be default" -- the workspace OPENS on the Kanban).
 * Board was made the FIRST tab on 2026-09-22 without moving the landing; this
 * moves it. `/portal/umed` with no params is the Kanban board, and My work is
 * `?view=work`, written explicitly by path() and by every link that means My
 * work (notifications, the send-back notice, the packet emails, the PA's
 * "Open the UMED").
 */
const DEFAULT_VIEW: View = 'board';

interface Loc { view: View; caseId: string | null; q: string; page: number; lens: LensKey | null; kind: ExceptionKey | null; whose: 'mine' | 'all'; tab: BoardTabKey | null;
  /** The Board view's layout: absent = the Kanban, the default since 2026-10-14; 'table' = the table (boardLayoutOf). */
  layout?: Layout | null; kOwner?: string | null; kSort?: string | null; kDir?: string | null }
type Layout = 'table' | 'kanban';
/** One case id however it is written: UUIDs are case-insensitive, and a pasted ?case= may be upper case (2026-10-14). */
const sameId = (a: string | null | undefined, b: string | null | undefined): boolean => Boolean(a) && Boolean(b) && String(a).toLowerCase() === String(b).toLowerCase();
/**
 * The Kanban is the default layout (Isaiah Cotto, 2026-10-14: "The kanban views should be default"); `layout=table`
 * selects the table, and a `layout=kanban` link still works. Until then the table was the default (2026-10-13).
 */
const DEFAULT_LAYOUT: Layout = DEFAULT_BOARD_LAYOUT;

/**
 * The default lens and exception kind. A URL carries them only when they
 * differ, so the address bar stays readable and a shared link says what was
 * actually chosen rather than restating every default.
 */
/**
 * Where a UMED creator starts (cutover 2026-09-21, EP5-UX-004): Needs creation
 * is the work — documents in hand, packet to make — and the runbook's first
 * line. Needs DCF account stays one chip away.
 */
const DEFAULT_LENS: LensKey = 'needs-creation';
const DEFAULT_KIND: ExceptionKey = 'reversal';

function path(l: Partial<Loc>): string {
  const qp = new URLSearchParams();
  if (l.view && l.view !== DEFAULT_VIEW) qp.set('view', l.view);
  if (l.caseId) qp.set('case', l.caseId);
  if (l.q) qp.set('q', l.q);
  // On All cases an absent lens MEANS everything, so the default key is not
  // a default there and dropping it would silently widen a shared link.
  if (l.lens && (l.view === 'all' || l.lens !== DEFAULT_LENS)) qp.set('lens', l.lens);
  if (l.kind && l.kind !== DEFAULT_KIND) qp.set('kind', l.kind);
  if (l.whose && l.whose !== 'mine') qp.set('whose', l.whose);
  // The board tab is the table's state and nobody else's, so it only ever rides on ?view=board&layout=table (a tab
  // without a layout reads as the table: boardLayoutOf).
  if (l.view === 'board' && (l.layout || DEFAULT_LAYOUT) === 'table' && l.tab && l.tab !== DEFAULT_BOARD_TAB) qp.set('tab', l.tab);
  // The layout, and the Kanban's own toolbar, ride on ?view=board only.
  if (l.view === 'board' && l.layout && l.layout !== DEFAULT_LAYOUT) qp.set('layout', l.layout);
  if (l.view === 'board' && (l.layout || DEFAULT_LAYOUT) === 'kanban') {
    if (l.kOwner) qp.set('owner', l.kOwner);
    if (l.kSort) qp.set('sort', l.kSort);
    if (l.kSort && l.kDir === 'desc') qp.set('dir', 'desc');
  }
  if (l.page && l.page > 1) qp.set('page', String(l.page));
  const s = qp.toString();
  return s ? `${BASE}?${s}` : BASE;
}

/* ── States ────────────────────────────────────────────────────────────── */

interface Refusal { title: string; body: string; detail?: string }

const REFUSALS: Record<string, Refusal> = {
  DENY_NO_CAPABILITY: {
    title: 'Your UMED role has not been ratified yet',
    body: 'Your account exists, but no UMED role on it has been put into effect. Ratification is a deliberate human step with a named approver. Ask an administrator.',
    detail: 'Decision: DENY_NO_CAPABILITY',
  },
  EMERGE_PHI_ACCESS_DENIED: {
    title: 'This workspace is not open to your account yet',
    body: 'During the pilot, access to eMerge healthcare screens is limited to a named list. Being signed in is not the same as being on it. Ask an administrator to add you.',
    detail: 'Decision: EMERGE_PHI_ACCESS_DENIED',
  },
};
const GENERIC: Refusal = { title: 'You do not have access to this', body: 'The server declined this request. If you believe that is wrong, ask an administrator to check your role assignment.' };
const OFFLINE: Refusal = { title: 'Could not reach the server', body: 'This is a connection problem rather than a permissions one. Nothing was changed — this workspace only reads. Try again in a moment.' };

const refusalFor = (code?: string | null): Refusal => REFUSALS[String(code || '').replace(/^AUTHZ_/, '')] || GENERIC;

function StateBlock({ refusal, retry }: { refusal: Refusal; retry?: () => void }) {
  return (
    <div className="um-state">
      <h2>{refusal.title}</h2>
      <p>{refusal.body}</p>
      {retry && <div className="btns"><button type="button" onClick={retry}>Try again</button></div>}
      {refusal.detail && <p className="detail">{refusal.detail}</p>}
    </div>
  );
}

function Skeleton({ announce = true }: { announce?: boolean }) {
  return (
    <section className="um-card" role={announce ? 'status' : undefined}>
      {announce && <span className="um-sr-only">Loading UMED work…</span>}
      {[0, 1, 2, 3].map((i) => <div className="um-skel" key={i} />)}
    </section>
  );
}

/* ── Small pieces ──────────────────────────────────────────────────────── */

/** Overdue is a stop. An unproven approval is attention. Everything else is a fact. */
const tone = (a: Attention) => (a.code === 'FOLLOW_UP_OVERDUE' ? 'stop' : a.code === 'APPROVAL_EVIDENCE_UNKNOWN' || a.code === 'EXCEPTION' ? 'attn' : '');

function AttentionTags({ items, superseded }: { items: Attention[]; superseded?: boolean }) {
  // A board exception older than a proven approval is history, not work: it
  // belongs under provenance, never as a tag saying something is required.
  const shown = superseded ? items.filter((a) => a.code !== 'EXCEPTION') : items;
  if (shown.length === 0) return null;
  const list = shown;
  return (
    <span className="um-tags">
      {list.map((a, i) => (
        <span key={`${a.code}-${i}`} className={`um-tag ${tone(a)}`} title={a.detail}>
          {/* The condition, not the category (packet §21): which document, which work. */}
          {a.code === 'MISSING_EVIDENCE'
            ? BLOCKER_LABEL[a.detail as keyof typeof BLOCKER_LABEL] || a.detail
            : a.code === 'EXCEPTION'
              ? EXCEPTION_WORK_LABEL[a.detail as keyof typeof EXCEPTION_WORK_LABEL] || ATTENTION_LABEL.EXCEPTION
              : ATTENTION_LABEL[a.code]}
        </span>
      ))}
    </span>
  );
}

function dueWords(a: OpenAction | null): { text: string; stop: boolean } {
  if (!a || !a.due_on) return { text: 'No date', stop: false };
  const d = daysFromToday(a.due_on);
  return { text: `${shortDate(a.due_on)} · ${relativeDay(a.due_on)}`, stop: d !== null && d < 0 };
}

function OwnerLine({ owner, claimable = false }: { owner: UmedCase['owner']; claimable?: boolean }) {
  if (claimable) return <span className="um-tag attn">Unclaimed · any operator can take it</span>;
  if (owner.kind === 'ASSIGNED') return <>{owner.name || 'Owner not named'}</>;
  if (owner.kind === 'NO_ACCOUNT') return <>{owner.name} <span className="um-quiet">· no eMerge account</span></>;
  if (owner.kind === 'UNRESOLVED') return <span className="um-quiet">Owner not on the roster</span>;
  return <span className="um-quiet">Unassigned</span>;
}

/** Take the list away (2026-09-21): the rows the screen is showing, as a CSV. */
function ExportButton({ what, run }: { what: string; run: () => Promise<string | null> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="um-export">
      <button type="button" className="um-open" disabled={busy} onClick={async () => {
        setBusy(true); setErr(null);
        try { setErr(await run()); } catch { setErr('Could not download the file.'); } finally { setBusy(false); }
      }}>{busy ? 'Preparing…' : `Export ${what}`}</button>
      {err && <span className="um-quiet stop" role="alert">{err}</span>}
    </div>
  );
}

/**
 * Search as you type (Josie Evans, round 4, 2026-10-07, point 2). The list answers ~250 ms after she stops
 * typing, from two characters (one letter matches half the building); the server does the matching -- case,
 * accents, spacing and word order forgiven -- and the board cancels a request a newer keystroke made stale.
 * Enter still searches at once, whatever the length. A live search replaces the history entry instead of
 * adding one per keystroke; Enter and Clear add one, as before.
 */
export const SEARCH_DEBOUNCE_MS = 250;
export const SEARCH_MIN_CHARS = 2;
export function SearchBar({ value, onSearch, placeholder = 'Search by resident, facility or case reference' }: {
  value: string; onSearch: (q: string, live?: boolean) => void; placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const box = React.useRef<HTMLInputElement | null>(null);
  const timer = React.useRef<number | null>(null);
  const cancel = () => { if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => cancel, []);
  const typed = (next: string) => {
    setDraft(next);
    cancel();
    const t = next.trim();
    // Nothing to ask yet: one character, or the same words as the list already answers.
    if (t === value.trim() || (t.length > 0 && t.length < SEARCH_MIN_CHARS)) return;
    timer.current = window.setTimeout(() => { timer.current = null; onSearch(t, true); }, SEARCH_DEBOUNCE_MS);
  };
  return (
    <form className="um-search" role="search" onSubmit={(e) => { e.preventDefault(); cancel(); onSearch(draft.trim()); }}>
      <input type="search" value={draft} ref={box} onChange={(e) => typed(e.target.value)}
        placeholder={placeholder} aria-label={placeholder} />
      <button type="submit">Search</button>
      {/* Clear removes itself, so focus goes to the box it cleared, not the page body (design verification P2-1). */}
      {value && <button type="button" className="um-clear" onClick={() => { cancel(); setDraft(''); onSearch(''); box.current?.focus(); }}>Clear</button>}
    </form>
  );
}

/**
 * A chip carries two numbers: what you hold, and what there is. One number
 * could only ever mean one of them, and a reader has no way to tell which —
 * which is how a chip reading 4 and a colleague's reading 135 were both
 * correct on the same morning about the same question.
 */
function LensBar({ scope, lens, onLens, basis = 'mine' }: {
  scope: Scope; lens: LensKey | null; onLens: (l: LensKey | null) => void; basis?: 'mine' | 'scope';
}) {
  const mine = scope.counts.lenses;
  const all = scope.counts.lenses_all || {};
  // All cases is not filtered by ownership, so one number is the honest one
  // there: the queue across everything this operator may see.
  const wide = basis === 'scope';
  const primary = scope.lenses.filter((l) => l.primary);
  const more = scope.lenses.filter((l) => !l.primary);
  const inMore = more.some((l) => l.key === lens);
  const label = (k: LensKey) => {
    const m = mine[k];
    const a = all[k];
    if (m === undefined && a === undefined) return null;
    if (wide) return <span className="um-count">{a ?? 0}</span>;
    if (a === undefined || a === m) return <span className="um-count">{m}</span>;
    return <span className="um-count" title={`${m ?? 0} you hold · ${a} in your scope`}>{m ?? 0}<span className="um-of"> / {a}</span></span>;
  };
  const text = (k: LensKey) => {
    const m = mine[k];
    const a = all[k];
    if (m === undefined && a === undefined) return '';
    if (wide) return ` · ${a ?? 0}`;
    return a === undefined || a === m ? ` · ${m}` : ` · ${m ?? 0} of ${a}`;
  };
  return (
    <div className="um-lenses" role="group" aria-label="View">
      {wide && (
        <button type="button" className={`um-lens${lens === null ? ' on' : ''}`} aria-pressed={lens === null}
          title={`Every case in your scope, in no particular queue.\n\n${scope.counts.all_cases} in your scope.`}
          onClick={() => onLens(null)}>
          Every case<span className="um-count">{scope.counts.all_cases}</span>
        </button>
      )}
      {primary.map((l) => (
        <button key={l.key} type="button" className={`um-lens${lens === l.key ? ' on' : ''}`} aria-pressed={lens === l.key}
          title={wide
            ? `${l.what}\n\n${all[l.key] ?? 0} in your scope.`
            : `${l.what}\n\n${mine[l.key] ?? 0} you hold · ${all[l.key] ?? 0} in your scope.`}
          onClick={() => onLens(l.key)}>
          {l.label}{label(l.key)}
        </button>
      ))}
      <label className={`um-lens-more${inMore ? ' on' : ''}`}>
        <span className="um-sr-only">More views</span>
        <select value={inMore && lens ? lens : ''} onChange={(e) => e.target.value && onLens(e.target.value as LensKey)} aria-label="More views">
          <option value="" disabled>More…</option>
          {more.map((l) => <option key={l.key} value={l.key}>{l.label}{text(l.key)}</option>)}
        </select>
      </label>
      <p className="um-lens-note">{wide
        ? 'Each count is every case in your scope, whoever holds it. My work narrows the same queues to yours.'
        : 'Each count is what you hold, then everything in your scope. Open All cases for the same queue, wider.'}</p>
    </div>
  );
}

/**
 * Said only when it matters, and only about eMerge's own update (2026-10-12):
 * Monday's retired sync never makes a workspace "not current".
 */
function FreshnessNote({ counts }: { counts: Counts }) {
  const text = notCurrentText(counts);
  if (!text) return null;
  return <div className="um-note um-fresh" role="status"><span><strong>Not current.</strong> {text}</span></div>;
}

function Pager({ page, limit, total, onPage }: { page: number; limit: number; total: number; onPage: (p: number) => void }) {
  if (total <= limit && page === 1) return null;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const last = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="um-pager">
      <span className="num">{from}–{to} of {total}</span>
      <div className="btns">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1}>Previous</button>
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= last}>Next</button>
      </div>
    </div>
  );
}

/* ── Rows ──────────────────────────────────────────────────────────────── */

/**
 * What is being produced or sent, and why (packet §7, §39). The creation
 * reason is the work type when there is one; otherwise the case's state.
 * Evidence gaps are what eMerge can PROVE missing — never what it merely
 * cannot see, which the panel says separately.
 */
function workType(c: UmedCase): string {
  return c.creation_reason ? CREATION_REASON_LABEL[c.creation_reason] : UMED_TYPE_LABEL[c.umed_type];
}

function EvidenceGaps({ c }: { c: UmedCase }) {
  // PR #420 round 8 (L3): a CLOSED readiness (the corrected re-send reached DCF, or was cancelled) has nothing left to
  // gather -- no gap chips.
  if (c.readiness?.state === 'CLOSED') return null;
  // When the server says what the account is to this work, DcfLine carries it and the gap is not repeated.
  const missing = (c.readiness?.missing || []).filter((m) => m !== 'DCF_ACCOUNT' || !c.dcf_account?.need);
  if (missing.length === 0) return null;
  return (
    <span className="um-gaps" aria-label="Evidence gaps">
      {missing.map((m) => <span key={m} className="um-tag attn">{REQUIREMENT_LABEL[m] || m}</span>)}
    </span>
  );
}

/**
 * The DCF account on the row (April, 2026-09-18): needed, or the email of the
 * account that exists. Not a view of its own, and never the password — that
 * is one audited click in the case panel.
 */
function DcfLine({ c }: { c: UmedCase }) {
  const need: DcfNeed | undefined = c.dcf_account?.need;
  if (!need) return null;
  if (need === 'NEEDED') return <span className="um-tag attn" aria-label="DCF account">DCF account needed</span>;
  if (need === 'ISSUE') return <span className="um-tag stop" aria-label="DCF account">DCF account could not be created</span>;
  if (need === 'PROVISIONED') return <span className="um-quiet" aria-label="DCF account">DCF · {c.dcf_account?.email || 'account recorded'}{c.dcf_account?.existing_case_id ? ' (existing)' : ''}</span>;
  return null;
}

function CaseRow({ c, selected, onOpen }: { c: UmedCase; selected: string | null; onOpen: (id: string) => void }) {
  const due = dueWords(c.next_action);
  const approval = approvalLine(c.approval);
  return (
    <li className={`um-row${selected === c.id ? ' on' : ''}`}>
      <div className="who">
        <strong>{personName(c)}</strong>
        <span><BuildingOrClinic row={c} fallback="Facility not recorded" /></span>
      </div>
      <div className="state">
        <strong>{workType(c)}</strong>
        <span>{stateLine(c)}</span>
        {financeLine(c) && <span className="um-sub">{financeLine(c)}</span>}
      </div>
      <div className="next">
        <strong>{c.next_action ? ACTION_LABEL[c.next_action.action_type] : 'No open obligation'}</strong>
        <span className={due.stop ? 'um-tag stop' : ''}>{c.next_action ? due.text : approval.text}</span>
      </div>
      <div className="flags">
        {c.claimable && <span className="um-tag attn">Unclaimed</span>}
        {/* The disposition word (2026-10-02): how a case in All cases says why it left the queues. */}
        <DispositionChip disposition={c.disposition} />
        <AttentionTags items={c.attention} superseded={Boolean(c.effective_state?.provenance?.superseded_exception)} /><EvidenceGaps c={c} /><DcfLine c={c} />
      </div>
      <button type="button" className="um-open" aria-pressed={selected === c.id} onClick={() => onOpen(c.id)}>Open</button>
    </li>
  );
}

function WorkList({ data, scope, lens, selected, onOpen, onPage }: {
  data: WorkPage; scope: Scope; lens: LensKey; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void;
}) {
  const def = scope.lenses.find((l) => l.key === lens);
  const c = scope.counts;
  return (
    <section className="um-card">
      <div className="um-card-head">
        <div><h2>My work</h2><p className="why">{def?.what}</p></div>
        <span className="um-count">{data.total}</span>
      </div>
      {data.items.length === 0 ? (
        <div className="um-empty">
          {c.my_open_cases === 0 ? (
            <>
              <p><strong>No UMED case names you as its operator.</strong></p>
              <p>
                {c.unowned > 0
                  ? `The board names a sender on each request, and eMerge resolved an active employee for some of them. ${c.unowned.toLocaleString()} open cases have no owner it could resolve — that is a migration gap, not an empty day. Approval checks shows the dated work whoever owns it.`
                  : 'That is not the same as no work existing. Approval checks shows every dated obligation in your scope.'}
              </p>
            </>
          ) : <p>Nothing you hold matches this view. {data.total === 0 ? `You hold ${c.my_open_cases} open ${c.my_open_cases === 1 ? 'case' : 'cases'} in total — try Everything.` : 'Nothing on this page.'}</p>}
        </div>
      ) : <ul className="um-rows">{data.items.map((r) => <CaseRow key={r.id} c={r} selected={selected} onOpen={onOpen} />)}</ul>}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/**
 * Josie's operating surface (packet §19): the dated approval reviews.
 * Overdue is not filtered away and not apologised for: an overdue review IS
 * the obligation, and the row says how late. Each row answers who, where,
 * what was sent, when, when it was last reviewed, when next, and the two
 * approval facts.
 */
function FollowUpList({ data, counts, whose, onWhose, selected, onOpen, onPage }: {
  data: FollowUpPage; counts: Counts; whose: 'mine' | 'all'; onWhose: (w: 'mine' | 'all') => void;
  selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void;
}) {
  const overdue = whose === 'mine' ? counts.follow_ups_overdue : counts.follow_ups_all_overdue;
  return (
    <section className="um-card">
      <div className="um-card-head">
        <div>
          <h2>Approval checks</h2>
          <p className="why">NOCA and FLMMIS are re-checked every {data.recheck_days} days after a UMED goes to DCF, until FLMMIS is reflected. Overdue first.</p>
        </div>
        <span className={`um-count${overdue > 0 ? ' stop' : ''}`}>{overdue > 0 ? `${overdue} overdue` : data.total}</span>
      </div>
      <div className="um-switch" role="group" aria-label="Whose reviews">
        <button type="button" className={whose === 'mine' ? 'on' : ''} aria-pressed={whose === 'mine'} onClick={() => onWhose('mine')}>
          Mine<span className="um-count">{counts.follow_ups}</span>
        </button>
        <button type="button" className={whose === 'all' ? 'on' : ''} aria-pressed={whose === 'all'} onClick={() => onWhose('all')}>
          Everyone's<span className="um-count">{counts.follow_ups_all}</span>
        </button>
      </div>
      {data.items.length === 0 ? (
        <div className="um-empty">
          {counts.follow_ups_all === 0
            ? <p>No UMED in your scope is waiting on DCF with a check due. Checks are opened by the projector when a UMED is submitted — not by this screen.</p>
            : <p>None of the {counts.follow_ups_all} dated checks in your scope is yours. Switch to Everyone's to see them.</p>}
        </div>
      ) : (
        <ul className="um-rows">
          {data.items.map((f) => (
            <li key={f.action_id} className={`um-row${selected === f.id ? ' on' : ''}`}>
              <div className="who"><strong>{personName(f)}</strong><span><BuildingOrClinic row={f} fallback="Facility not recorded" /></span></div>
              <div className="state">
                <strong>{f.action_type === 'CHECK_NOCA_FLMMIS' ? workType(f) : ACTION_LABEL[f.action_type]}</strong>
                <span>
                  Submitted {shortDate(f.sent_to_dcf_on || f.submitted_on)}
                  {(f.review?.last_reviewed_on || f.last_follow_up_on) ? ` · last reviewed ${shortDate(f.review?.last_reviewed_on || f.last_follow_up_on)}` : ' · not yet reviewed'}
                </span>
              </div>
              <div className="next">
                <strong className={f.past_due ? 'um-tag stop' : ''}>Next review {shortDate(f.due_on)}</strong>
                <span>{f.past_due ? `${f.days_late} ${f.days_late === 1 ? 'day' : 'days'} late` : relativeDay(f.due_on)}</span>
              </div>
              <div className="flags um-evidence">
                <span><em>NOCA</em> {NOCA_LABEL[f.approval.noca]}</span>
                <span><em>FLMMIS</em> {FLMMIS_LABEL[f.approval.flmmis]}</span>
              </div>
              <button type="button" className="um-open" aria-pressed={selected === f.id} onClick={() => onOpen(f.id)}>Open</button>
            </li>
          ))}
        </ul>
      )}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/**
 * Approved holds evidence-backed approvals and nothing else. Today that is
 * an empty list with 2,564 source claims behind it, and the empty state says
 * exactly that — because "no data" and "nothing has been proven" are
 * different sentences, and only one of them is true here.
 */
function ApprovedList({ data, financeVisible, selected, onOpen, onPage, onConflicts }: {
  data: ApprovedPage; financeVisible: boolean; selected: string | null;
  onOpen: (id: string) => void; onPage: (p: number) => void; onConflicts: () => void;
}) {
  return (
    <section className="um-card">
      <div className="um-card-head">
        <div><h2>Approved</h2><p className="why">Cases a UMED operator moved to Approved, with FLMMIS reflected on record. FLMMIS alone, or a source word, never reaches this list.</p></div>
        <span className="um-count">{data.total}</span>
      </div>
      {data.items.length === 0 ? (
        <div className="um-empty">
          <p><strong>No case has been approved here yet.</strong></p>
          <p>
            A case is approved when a UMED operator moves it to Approved, which needs FLMMIS reflected on record first — FLMMIS is what lets the facility bill. The Monday board's word is not that act. Approving a case is what puts it here.
          </p>
          {data.source_claimed > 0 && (
            <p>
              {data.source_claimed.toLocaleString()} open cases claim approval at the source and cannot show it.{' '}
              <button type="button" className="um-link" onClick={onConflicts}>See them under Exceptions</button>. This is a records gap, not a judgement about anyone's work.
            </p>
          )}
        </div>
      ) : (
        <>
          {data.finance_missing > 0 && financeVisible && (
            <div className="um-note">
              <span><strong>{data.finance_missing} approved {data.finance_missing === 1 ? 'case has' : 'cases have'} never reached Finance.</strong> Not a missing date: Finance holds no row, no handoff is open, and no account exists. Each one is an exception somebody has to work.</span>
            </div>
          )}
          <ul className="um-approved-rows">{data.items.map((r) => <ApprovedRow key={r.id} c={r} selected={selected} onOpen={onOpen} />)}</ul>
        </>
      )}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/**
 * One approved case (Josie Evans, 2026-10-06): the patient's name, Approved,
 * and the date it was approved -- the act's own date. Nothing else on the row:
 * no work type, no Finance line, no next action. Open shows the rest.
 */
function ApprovedRow({ c, selected, onOpen }: { c: UmedCase; selected: string | null; onOpen: (id: string) => void }) {
  return (
    <li className={`um-approved-row${selected === c.id ? ' on' : ''}`}>
      <span className="who">{personName(c)}</span>
      <FilledStatusChip word="APPROVED" column={UMED_STATUS} label="Approved" size="sm" />
      <span className="when">{c.approved_on ? shortDate(c.approved_on) : <span className="um-quiet">Date not recorded</span>}</span>
      <button type="button" className="um-open" aria-pressed={selected === c.id} aria-label={`Open ${personName(c)}`} onClick={() => onOpen(c.id)}>Open</button>
    </li>
  );
}

function ExceptionsPanel({ scope, kind, data, selected, onKind, onOpen, onPage }: {
  scope: Scope; kind: ExceptionKey; data: ExceptionPage | null; selected: string | null;
  onKind: (k: ExceptionKey) => void; onOpen: (id: string) => void; onPage: (p: number) => void;
}) {
  const counts = scope.counts.exceptions;
  return (
    <section className="um-card">
      <div className="um-card-head">
        <div><h2>Exceptions</h2><p className="why">One kind at a time. A reversal is not a correction, and neither is an approval-evidence conflict.</p></div>
      </div>
      <div className="um-kinds" role="group" aria-label="Exception kind">
        {scope.exception_kinds.map((e) => (
          <button key={e.key} type="button" className={`um-lens${kind === e.key ? ' on' : ''}`} aria-pressed={kind === e.key} title={e.what} onClick={() => onKind(e.key)}>
            {e.label}<span className="um-count">{counts[e.key] ?? 0}</span>
          </button>
        ))}
      </div>
      {data && <p className="um-kind-what">{data.what}</p>}
      {!data ? <Skeleton announce={false} />
        : data.items.length === 0 ? <p className="um-empty">Nothing in your scope is in this state.</p>
          : <ul className="um-rows">{data.items.map((r) => <CaseRow key={r.id} c={r} selected={selected} onOpen={onOpen} />)}</ul>}
      {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />}
    </section>
  );
}

function AllList({ data, selected, onOpen, onPage }: { data: Paged<UmedCase>; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void }) {
  return (
    <section className="um-card">
      <div className="um-card-head">
        <div><h2>All cases</h2><p className="why">Every UMED case in your scope, for retrieval and history. Not the work queue.</p></div>
        <span className="um-count">{data.total}</span>
      </div>
      {data.items.length === 0
        ? <p className="um-empty">No case in your scope matches.</p>
        : <ul className="um-rows">{data.items.map((r) => <CaseRow key={r.id} c={r} selected={selected} onOpen={onOpen} />)}</ul>}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/* ── Case context ──────────────────────────────────────────────────────── */

const NotRecorded = () => <span className="um-quiet">Not recorded</span>;

/**
 * NOCA and FLMMIS, first-class and separate, each with its own word. They
 * are never collapsed into one Approved field, because that collapse is
 * what the board did and what this workspace exists to undo.
 */
function ApprovalSection({ a, visible, nocaEvidence = null, act = null }: { a: Approval; visible: boolean; nocaEvidence?: NocaEvidence | null; act?: React.ReactNode }) {
  const line = approvalLine(a);
  // UM-R3 (2026-10-02): a NOCA eMerge holds that nobody has recorded reading is not "Not recorded".
  const nocaOnFile = nocaEvidence === 'DOCUMENT_ON_FILE';
  return (
    <div className="um-section">
      <h3 className="rs-dup">Approval</h3>
      <p className={`um-verdict ${line.tone}`}>{line.text}</p>
      {a.canonical === 'PROVEN' && (a.approved_by_name || a.approved_at) && (
        <p className="um-quiet">Moved to Approved{a.approved_by_name ? ` by ${a.approved_by_name}` : ''}{a.approved_at ? ` on ${shortDate(a.approved_at)}` : ''}.</p>
      )}
      {a.ready_to_approve && (
        <p className="um-quiet">NOCA received and FLMMIS reflected are on record. The case is not approved until you move it to Approved; that is what opens the Finance handoff.</p>
      )}
      {act}
      {visible ? (
        <>
          <dl className="um-evidence-grid">
            <div><dt>NOCA</dt><dd className={a.noca === 'RECEIVED' ? 'ok' : nocaOnFile ? 'attn' : ''}>{nocaOnFile ? 'On file · not yet reviewed' : NOCA_LABEL[a.noca]}</dd></div>
            <div><dt>FLMMIS</dt><dd className={a.flmmis === 'REFLECTED' ? 'ok' : ''}>{FLMMIS_LABEL[a.flmmis]}</dd></div>
          </dl>
          {a.canonical === 'PROVEN' && a.basis === 'FLMMIS_ONLY' && (
            <p className="um-quiet">Approved on FLMMIS alone: FLMMIS is what lets the facility bill. The NOCA is {NOCA_LABEL[a.noca].toLowerCase()} and stays recorded as its own fact.</p>
          )}
          {a.conflict && (
            <p className="um-quiet">
              The source records this as approved{a.source_status_raw ? ` (“${a.source_status_raw}”)` : ''}, and eMerge has not approved it:{' '}
              {a.missing.some((m) => m.fact === 'APPROVAL')
                ? 'NOCA received and FLMMIS reflected are on record, and nobody has moved it to Approved here yet.'
                : `it does not hold ${a.missing.map((m) => (m.fact === 'NOCA' ? 'NOCA received' : 'FLMMIS reflected')).join(' or ') || 'the evidence'}. Approval needs NOCA received and FLMMIS reflected on record, then the move to Approved.`}
              {' '}It is not counted as approved and no Finance handoff follows from it.
            </p>
          )}
          {!a.conflict && a.canonical === 'NOT_PROVEN' && !a.ready_to_approve && (
            <p className="um-quiet">Not claimed as approved by the source either. Nothing is missing that should be here yet.</p>
          )}
        </>
      ) : <p className="um-quiet">The two approval facts are not shown to your role. The verdict above is still the canonical one.</p>}
    </div>
  );
}

/* ── Phase 3: the native acts ──────────────────────────────────────────── */

/** A refusal from a write, said in the server's words. */
function outcomeOf(r: { success: boolean; error?: string; message?: string; code?: string; status?: number }): string {
  // Not every failure is a refusal, and calling an outage one tells the
  // operator to go and ask for permissions they already hold. No status means
  // the server was never reached; a 5xx means it was reached and broke.
  if (r.status === undefined) return 'Could not reach the server. Nothing was recorded.';
  if (r.status >= 500) return 'The server failed while recording this. Nothing was recorded, and it is not a permissions problem.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
}

/**
 * Josie's act (packet §20–23). She records what NOCA and FLMMIS show and
 * whether the packet must go back. Since 2026-10-06 that is evidence only:
 * the approval is her separate act, the Approve button beside the verdict.
 */
/*
 * RESUBMIT is offered only where the server will open a resubmission (verification of b9bcc9e, P2): the review is
 * one transaction, so a refused RESUBMIT lost the whole review -- a NOCA she was recording included. Where it cannot
 * be opened, the form says why in one line. Whatever she entered stays in the form on any refusal.
 */
function ReviewForm({ caseId, onDone, resubmission = null, currentNoca = 'UNKNOWN' }: {
  caseId: string; onDone: (msg: string) => Promise<void>; resubmission?: { can_open: boolean; why: string | null; flmmis_only?: boolean } | null;
  /** The NOCA on the case now: the form starts from it, so a review that does not touch the NOCA does not send "not recorded" over it (D2). */
  currentNoca?: Noca;
}) {
  // A review decides on the FLMMIS it records, so a case refused only for its recorded FLMMIS still offers RESUBMIT here (R3-P2b); the server judges.
  const canResubmit = !resubmission || resubmission.can_open || Boolean(resubmission.flmmis_only);
  const [noca, setNoca] = useState<NocaResult>(nocaForward(currentNoca) || 'NOT_RECORDED');
  const [flmmis, setFlmmis] = useState<FlmmisResult>('NOT_RECORDED');
  const [decision, setDecision] = useState<'CONTINUE' | 'RESUBMIT'>('CONTINUE');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 2026-10-06: FLMMIS reflected is evidence; moving the case to Approved is a separate act.
  const approves = flmmis === 'REFLECTED';
  // Approval needs BOTH facts (2026-10-13): the helper and the notice say "NOCA received and FLMMIS reflected" only when both are on this form.
  const bothOnRecord = approves && noca === 'RECEIVED';
  const formRef = React.useRef<HTMLFormElement | null>(null);
  return (
    <form className="um-form" aria-label="Record an approval review" ref={formRef} onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        // The Then menu is hidden once FLMMIS reflects, so a RESUBMIT picked before then must not travel with it (PR #362 F4).
        const r = await umedApi.review(caseId, { noca, flmmis, decision: approves || !canResubmit ? 'CONTINUE' : decision, note: note.trim() || undefined });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        await onDone(bothOnRecord
          ? 'Review recorded: NOCA received and FLMMIS reflected are on record. The case is not approved until you approve it — the Approve button is under Approval, above.'
          : approves
          ? 'Review recorded: FLMMIS reflected is on record. Approval also needs NOCA received — record it first. The case is not approved.'
          : decision === 'RESUBMIT' ? `Review recorded. The case is DCF RESUB NEEDED: the submitter has been told in eMerge${note.trim() ? ', with your note' : ''}.` : 'Review recorded. The next review is due in ten days.');
        // The act this enabled is above the form: bring it into view (design verification P2-10).
        if (bothOnRecord) {
          const act = formRef.current?.closest('[aria-label="Case context"]')?.querySelector<HTMLElement>('.um-approval-act .um-approve-btn');
          if (act && typeof act.scrollIntoView === 'function') act.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <label><span>NOCA</span>
        <select value={noca} onChange={(e) => setNoca(e.target.value as NocaResult)} aria-label="NOCA result">
          {(Object.keys(NOCA_RESULT_LABEL) as NocaResult[]).map((k) => <option key={k} value={k}>{NOCA_RESULT_LABEL[k]}</option>)}
        </select>
      </label>
      <label><span>FLMMIS</span>
        <select value={flmmis} onChange={(e) => { const v = e.target.value as FlmmisResult; setFlmmis(v); if (v === 'REFLECTED') setDecision('CONTINUE'); }} aria-label="FLMMIS result">
          {(Object.keys(FLMMIS_RESULT_LABEL) as FlmmisResult[]).map((k) => <option key={k} value={k}>{FLMMIS_RESULT_LABEL[k]}</option>)}
        </select>
      </label>
      {!approves && (
        <label><span>Then</span>
          <select value={decision} onChange={(e) => setDecision(e.target.value as 'CONTINUE' | 'RESUBMIT')} aria-label="Decision">
            <option value="CONTINUE">{DECISION_LABEL.CONTINUE}</option>
            {canResubmit && <option value="RESUBMIT">{DECISION_LABEL.RESUBMIT}</option>}
          </select>
        </label>
      )}
      {!approves && !canResubmit && resubmission?.why && (
        <p className="um-quiet um-resub-why">Resubmit is not offered: {resubmission.why}</p>
      )}
      {approves && (
        <p className="um-quiet">{bothOnRecord ? 'Records NOCA received and FLMMIS reflected. The case is not approved until you approve it under Approval.' : FLMMIS_EFFECT}</p>
      )}
      <label><span>Note (optional)</span><input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} aria-label="Review note" /></label>
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Recording…' : 'Record review'}</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * The DCF account (packet §13–17): assign the email, record the account
 * with its credential, or record that it could not be created and why. The
 * password goes to the server once, encrypted there, and comes back only
 * through the audited reveal — into component state, never storage.
 */
/**
 * T2 (2026-09-20, charter §4.1): the resident's SSN, for the DCF account and
 * the UMED itself. One click names the purpose; the server writes the reveal
 * row before it decrypts; the number lives in component state and is hidden
 * again on demand or when the case changes.
 */
function SsnReveal({ patientId, contextId, purpose, contextType }: { patientId: string; contextId: string | null; purpose: 'DCF_ACCOUNT' | 'UMED_PROCESSING'; contextType: 'UMED_CASE' | 'FINANCE_ACCOUNT' }) {
  const [held, setHeld] = useState<boolean | null>(null);
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { let live = true; (async () => { try { const r = await residentsApi.scope(); if (live) setHeld(Boolean(r.success && r.data && r.data.can.reveal_ssn)); } catch { if (live) setHeld(false); } })(); return () => { live = false; }; }, []);
  useEffect(() => () => setValue(null), [patientId]);
  if (!held) return null;
  return (
    <>
      <dt>SSN</dt>
      <dd>
        {value ? <span className="um-secret">{value}</span> : <span aria-label="SSN hidden">•••-••-••••</span>}
        {!value && <button type="button" className="um-open" style={{ marginLeft: 8 }} disabled={busy} onClick={async () => {
          setBusy(true); setErr(null);
          try { const r = await residentsApi.revealSsn(patientId, { purpose, context_type: contextType, context_id: contextId }); if (!r.success || !r.data) { setErr(r.message || r.error || 'The server refused the reveal.'); return; } setValue(r.data.ssn); }
          catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
        }}>Reveal SSN</button>}
        {value && <button type="button" className="um-link" style={{ marginLeft: 8 }} onClick={() => setValue(null)}>Hide</button>}
        {err && <span className="um-quiet stop" role="alert"> {err}</span>}
      </dd>
    </>
  );
}

export function DcfAccountPanel({ caseId, patientId, account, scope, onDone, benefits = null, createRequest = 0, closed = false, purpose = null }: {
  caseId: string; patientId: string | null; account: DcfAccountRecord; scope: Scope; onDone: (msg: string) => Promise<void>;
  /** What the case is sending now: the stuck mark records an account only where that work needs one (2026-10-13). */
  purpose?: string | null;
  /** Gwynn, 2026-10-12: the DCF benefits link on this case. */
  benefits?: DcfBenefits | null;
  /** A request (a fresh number each time) from the board's DCF EMAIL CREATED: open the record form. */
  createRequest?: number;
  closed?: boolean;
}) {
  // This case's email, else the resident's existing account's — so a correction records the account it reuses without retyping it.
  const [email, setEmail] = useState(account.email || account.existing?.email || '');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'none' | 'created' | 'unable'>('none');
  const section = React.useRef<HTMLDivElement | null>(null);
  const answered = React.useRef(0);
  const createdEmail = React.useRef<HTMLInputElement | null>(null);
  const createdPassword = React.useRef<HTMLInputElement | null>(null);
  // The email-created form takes focus on each ASK to open it (a click, or a board request even when it is already open) -- never on a mere mount.
  // Its first useful field: the password, or the email when none is on record yet.
  const [createdAsk, setCreatedAsk] = useState(0);
  useEffect(() => {
    if (createdAsk === 0 || mode !== 'created') return;
    const target = !email.trim() && createdEmail.current ? createdEmail.current : createdPassword.current;
    if (target) target.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAsk]);
  // DCF EMAIL CREATED from the board: the same record form as "DCF email created…" here, opened -- once per ask.
  useEffect(() => {
    if (!createRequest || answered.current === createRequest) return;
    answered.current = createRequest;
    if (canDo(scope, 'manage_dcf_account') && account.state !== 'ACCOUNT_CREATED') { setMode('created'); setCreatedAsk((n) => n + 1); }
    if (section.current && typeof section.current.scrollIntoView === 'function') section.current.scrollIntoView({ block: 'center' });
  }, [createRequest, scope, account.state]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ email: string; password: string } | null>(null);
  // UM-R4: the resident's login on another UMED, when the server refused a second one (RESIDENT_HAS_LOGIN).
  const [other, setOther] = useState<{ caseId: string; email: string } | null>(null);
  // Fresh state per case and per account state: the parent keys this panel
  // on both, so a change remounts it — and a revealed secret never survives
  // a change of case.
  const manage = canDo(scope, 'manage_dcf_account');
  const reveal = canDo(scope, 'reveal_dcf_credential');
  const run = async (fn: () => Promise<{ success: boolean; error?: string; message?: string; code?: string; detail?: any }>, msg: string) => {
    setBusy(true); setErr(null); setOther(null);
    try { const r = await fn(); if (!r.success) { setErr(outcomeOf(r)); if (r.code === 'RESIDENT_HAS_LOGIN' && r.detail && r.detail.caseId) setOther({ caseId: r.detail.caseId, email: r.detail.email }); return; } setPassword(''); setMode('none'); await onDone(msg); }
    catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <div className="um-section" ref={section}>
      <h3>DCF account</h3>
      <dl className="um-kv">
        {/* What the account is to this work (April, 2026-09-18): a new UMED needs a new account; a correction reuses the resident's. Derived from the records, never assumed. */}
        {account.need && (
          <><dt>For this work</dt><dd className={account.need === 'NEEDED' || account.need === 'ISSUE' ? 'stop' : ''}>
            {DCF_NEED_LABEL[account.need]}
            {account.need === 'PROVISIONED' && account.existing ? <span className="um-quiet"> · the resident's existing account, recorded on an earlier UMED</span> : null}
            {account.need === 'NEEDED' && account.state !== 'NEEDS_EMAIL' && account.state !== 'EMAIL_ASSIGNED' && account.source !== 'EMERGE' ? <span className="um-quiet"> · none recorded for this resident</span> : null}
          </dd></>
        )}
        <dt>Status</dt><dd className={account.state === 'UNABLE_TO_CREATE' ? 'stop' : ''}>{DCF_NATIVE_LABEL[account.state]}{account.source === 'MONDAY' && account.state !== 'NOT_REQUIRED' && account.state !== 'UNKNOWN' ? <span className="um-quiet"> · from the board</span> : null}</dd>
        <dt>Email</dt><dd>{account.email || account.existing?.email || <NotRecorded />}</dd>
        {patientId && <SsnReveal patientId={patientId} contextId={caseId} purpose="DCF_ACCOUNT" contextType="UMED_CASE" />}
        {account.state === 'UNABLE_TO_CREATE' && <><dt>Why</dt><dd>{account.unable_reason}</dd></>}
        {(account.state === 'ACCOUNT_CREATED' || (account.state === 'EMAIL_ASSIGNED' && account.has_credential)) && (
          <>
            <dt>Password</dt>
            <dd>
              {revealed ? <span className="um-secret">{revealed.password}</span> : <span aria-label="Password hidden">••••••••••</span>}
              {/* One click (UD-25): the team logs in to DCF on the resident's behalf all day. The server still writes the audit event before it decrypts. */}
              {reveal && !revealed && (
                <button type="button" className="um-open" style={{ marginLeft: 8 }} disabled={busy} onClick={async () => {
                  setBusy(true); setErr(null);
                  try { const r = await umedApi.revealDcfCredential(caseId, 'DCF portal login'); if (!r.success || !r.data) { setErr(outcomeOf(r)); return; } setRevealed({ email: r.data.email, password: r.data.password }); }
                  catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
                }}>Reveal password</button>
              )}
              {revealed && <button type="button" className="um-link" style={{ marginLeft: 8 }} onClick={() => setRevealed(null)}>Hide</button>}
            </dd>
          </>
        )}
      </dl>
      {account.state === 'UNABLE_TO_CREATE' && <p className="um-quiet">With no DCF account, approval review can only watch FLMMIS for this case. FLMMIS reflected is evidence: the case is approved when you move it to Approved.</p>}
      {/* Ruling, Isaiah Cotto 2026-10-13: an account that cannot be created does not hold the fax. */}
      {account.state === 'UNABLE_TO_CREATE' && purposeNeedsDcfAccount(purpose) && <p className="um-quiet" data-testid="dcf-unable-fax">The packet can still be faxed: Fax to DCF, or Record fax sending, in the Submission section. It cannot be uploaded.</p>}
      {account.state === 'ACCOUNT_CREATED' && (account.recorded_by_name || account.recorded_at) && (
        <p className="um-quiet" data-testid="dcf-email-created">DCF email created{account.recorded_by_name ? ` by ${account.recorded_by_name}` : ''}{account.recorded_at ? ` ${shortDateTime(account.recorded_at)}` : ''}.</p>
      )}
      {/* STUCK = account made (Isaiah Cotto, 2026-10-13): the stuck form says it records this account, and asks for what it needs. */}
      <DcfBenefitsLink caseId={caseId} benefits={benefits} canManage={canDo(scope, 'manage_dcf_account')} closed={closed} onDone={onDone} account={stuckFactsOfRecord(account, purpose)} />
      {manage && account.state !== 'ACCOUNT_CREATED' && (
        <div className="um-form" aria-label="DCF account">
          <label><span>DCF email</span><input ref={createdEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="DCF email" /></label>
          <div className="btns">
            {(account.state === 'NEEDS_EMAIL' || account.state === 'UNKNOWN' || email.trim().toLowerCase() !== (account.email || '')) && (
              <button type="button" className="um-open" disabled={busy || !email.trim()} onClick={() => run(() => umedApi.assignDcfEmail(caseId, email.trim()), 'Email assigned.')}>Assign email</button>
            )}
            {/* Gwynn's words (2026-10-12): "DCF Email Created" is this act -- the account made at DCF with the resident's email, recorded with who and when. */}
            <button type="button" className="um-open" disabled={busy} title="Record that the resident's DCF account was created with this email (who and when are kept; Finance sees it)."
              onClick={() => { if (mode !== 'created') setCreatedAsk((n) => n + 1); setMode(mode === 'created' ? 'none' : 'created'); }}>DCF email created…</button>
            <button type="button" className="um-open" disabled={busy} onClick={() => setMode(mode === 'unable' ? 'none' : 'unable')}>Could not create…</button>
          </div>
          {mode === 'created' && (
            <>
              <label><span>Password</span><input ref={createdPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" aria-label="DCF password" /></label>
              <p className="um-quiet">{account.has_credential ? 'Leave it blank to keep the password on record (a generated login). ' : ''}Stored encrypted. Seen again only through an audited reveal.</p>
              <div className="btns"><button type="button" className="um-open um-primary" disabled={busy || (password.length < 6 && !(account.has_credential && !password)) || !email.trim()} aria-describedby={(firstReason([!email.trim(), 'Enter the DCF account email first.'], [password.length < 6 && !(account.has_credential && !password), 'Enter a password of at least 6 characters first.'])) ? `why-dcf-acct-${caseId}` : undefined} onClick={() => run(() => umedApi.dcfAccountCreated(caseId, { email: email.trim(), ...(password ? { password } : {}) }), 'DCF account recorded.')}>Record account</button><Why id={`why-dcf-acct-${caseId}`} reason={firstReason([!email.trim(), 'Enter the DCF account email first.'], [password.length < 6 && !(account.has_credential && !password), 'Enter a password of at least 6 characters first.'])} className="um-text um-why" /></div>
            </>
          )}
          {mode === 'unable' && (
            <>
              <label><span>Why it could not be created</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Reason the account could not be created" /></label>
              <div className="btns"><button type="button" className="um-open um-primary" disabled={busy || reason.trim().length < 3} aria-describedby={(reason.trim().length < 3 && 'Say why (at least 3 characters) first.') ? `why-dcf-unable-${caseId}` : undefined} onClick={() => run(() => umedApi.dcfAccountUnable(caseId, reason.trim()), 'Recorded: the account could not be created.')}>Record</button><Why id={`why-dcf-unable-${caseId}`} reason={reason.trim().length < 3 && 'Say why (at least 3 characters) first.'} className="um-text um-why" /></div>
            </>
          )}
        </div>
      )}
      {other && (
        <p className="um-notice stop" role="alert">
          This resident already has a DCF login ({other.email}) on another UMED, so a second one was not recorded.{' '}
          <Link to={`/portal/umed?view=work&case=${other.caseId}`}>Open that case</Link>{' '}
          <button type="button" className="um-link" onClick={() => { setEmail(other.email); setOther(null); setErr(null); }}>Use that email here</button>
        </p>
      )}
      {err && !other && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * Corrections (packet §25–28): one of April's three types, a reason that is
 * required and kept, lineage to the original, and a completion that hands
 * off to Finance exactly once.
 */
/*
 * 2026-10-29 (Josie Evans: a PAID IN FULL CORRECTION completed by mistake, never sent): a correction completes here once
 * its corrected UMED went to DCF (`openSent`, the board row's sent_since_opened). Before that the completion is kept for
 * one that went to DCF another way, behind its own confirmation -- never the one-click "Correction completed". A paid in
 * full correction is requested with its note for Gwynn (required; the server refuses NOTE_REQUIRED), and the note is
 * shown with the correction's reasons.
 */
export const OUTSIDE_EMERGE_CONFIRM = 'Its corrected UMED went to DCF outside eMerge, and DCF shows it';
// The words moved to BoardTable (2026-10-29) so the row's "Already sent to DCF outside eMerge" says the same; re-exported.
export { correctionCompletedWords };
type Answer<T> = { success: boolean; error?: string; message?: string; code?: string; data?: T };
/*
 * The open correction's acts on the docked record (2026-11-03, SCRUM-127): until now SEND AGAIN and "Add a note for
 * Gwynn" lived only in the Table's status cell, and the Kanban -- the default layout -- opens the dock. The same panels
 * (CorrectionResendPanel, CorrectionNotePanel) behind the same predicates as the row (correctionResendOffered,
 * correctionHasNote). An act that cannot be asked now is drawn disabled with its reason, never left out.
 */
function OpenCorrectionActs({ row, act, onAct, onDone }: {
  row: UmedCase; act: 'resend' | 'note' | null; onAct: (a: 'resend' | 'note' | null) => void; onDone: (msg: string) => Promise<void>;
}) {
  const resendWhy = correctionResendHeld(row);
  // R2 (#580): offered AND no fax of the case in flight (correctionResendHeld then says why).
  const resendOk = correctionResendOffered(row) && !row.fax_in_flight;
  const noteOk = !correctionHasNote(row) && row.stage !== 'CLOSED';
  const noteWhy = noteOk ? null : row.stage === 'CLOSED' ? 'This case is closed.' : CORRECTION_NOTE_HAS_ONE;
  const done = (m: string) => { void onDone(m); };
  if (act === 'resend') return <CorrectionResendPanel row={row} onCancel={() => onAct(null)} onDone={done} />;
  if (act === 'note') return <CorrectionNotePanel row={row} onCancel={() => onAct(null)} onDone={done} />;
  return (
    <div className="um-form" aria-label="The open correction's acts" data-testid="dock-correction-acts">
      <div className="btns">
        <button type="button" className="um-open" disabled={!resendOk} aria-describedby={resendWhy ? 'um-dock-resend-why' : undefined}
          title={resendOk ? correctionResendEffect(row) : undefined} onClick={() => onAct('resend')}>{correctionResendLabel(row)}</button>
        <button type="button" className="um-open" disabled={!noteOk} aria-describedby={noteWhy ? 'um-dock-note-why' : undefined}
          title={noteOk ? CORRECTION_NOTE_EFFECT : undefined} onClick={() => onAct('note')}>{CORRECTION_NOTE_LABEL}</button>
      </div>
      {resendWhy && <p className="um-quiet" id="um-dock-resend-why" data-testid="dock-resend-why">{correctionResendLabel(row)}: {resendWhy}</p>}
      {noteWhy && <p className="um-quiet" id="um-dock-note-why" data-testid="dock-note-why">{CORRECTION_NOTE_LABEL}: {noteWhy}</p>}
    </div>
  );
}
function CorrectionsPanel({ caseId, corrections, scope, onDone, partialCorrectionId = null, plCorrectionId = null, resubmittalOpen = false, openSent = true, row = null }: { caseId: string; corrections: Correction[]; scope: Scope; onDone: (msg: string) => Promise<void>; partialCorrectionId?: string | null; plCorrectionId?: string | null; resubmittalOpen?: boolean; openSent?: boolean;
  /** The case as the dock read it (2026-11-03): the open correction's SEND AGAIN and note for Gwynn are decided from it, as on the row. */
  row?: UmedCase | null }) {
  const types = scope.correction_types || (['SFS_CORRECTION', 'PAID_IN_FULL_CORRECTION', 'SFS_CORRECTION_FLMMIS_ISSUE'] as CorrectionType[]);
  const [type, setType] = useState<CorrectionType>(types[0]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [more, setMore] = useState('');
  const [outside, setOutside] = useState(false);
  // Verification of #558 (O1): the server now requires the day it went and how (OUTSIDE_EVIDENCE_INVALID otherwise).
  const [outsideOn, setOutsideOn] = useState('');
  const [outsideHow, setOutsideHow] = useState('');
  const outsideReady = outside && /^\d{4}-\d{2}-\d{2}$/.test(outsideOn) && outsideOn <= todayLocal()
    && outsideHow.trim().length >= 3 && outsideHow.trim().length <= OUTSIDE_NOTE_MAX;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The open correction's own acts on the dock (2026-11-03, SCRUM-127): SEND AGAIN and a note for Gwynn, each its panel.
  const [act, setAct] = useState<'resend' | 'note' | null>(null);
  const manage = canDo(scope, 'manage_corrections');
  const open = corrections.find((k) => k.state !== 'COMPLETED') || null;
  const noteRequired = type === 'PAID_IN_FULL_CORRECTION';
  const requestReady = reason.trim().length >= 3 && (!noteRequired || note.trim().length >= 3);
  async function run<T>(fn: () => Promise<Answer<T>>, msg: string | ((d: T | undefined) => string)) {
    setBusy(true); setErr(null);
    try {
      const r = await fn(); if (!r.success) { setErr(outcomeOf(r)); return; }
      setReason(''); setNote(''); setMore(''); setOutside(false); setOutsideOn(''); setOutsideHow('');
      await onDone(typeof msg === 'function' ? msg(r.data) : msg);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  }
  if (corrections.length === 0 && !manage) return null;
  return (
    <div className="um-section">
      <h3>Corrections</h3>
      {corrections.length === 0 && <p className="um-quiet">None recorded in eMerge.</p>}
      {corrections.map((k) => (
        <div key={k.id} className="um-kv-block">
          <dl className="um-kv">
            <dt>{CORRECTION_TYPE_LABEL[k.correction_type]}</dt><dd>{k.state === 'COMPLETED' ? `completed ${shortDate(k.completed_at)}`
              // R4 (2026-10-28): its corrected UMED went to DCF (the latest sending); Josie reviews it about every 10 days.
              : k.state === 'SUBMITTED' && k.submitted_at ? `sent to DCF ${shortDate(k.submitted_at)}, awaiting review (requested ${shortDate(k.requested_at)})`
                // 2026-11-03: sent before #532 marked anything SUBMITTED -- the row's own word reads the sending.
                : row?.correction?.id === k.id && row.correction_status?.state === 'SENT' && row.correction_status.submitted_at
                  ? `sent to DCF ${shortDate(row.correction_status.submitted_at)}, awaiting review (requested ${shortDate(k.requested_at)})`
                : `requested ${shortDate(k.requested_at)}${k.requested_by_name ? ` by ${k.requested_by_name}` : ''}, to send`}</dd>
            {k.reasons.map((r, i) => <React.Fragment key={r.id}><dt>{i === 0 ? 'Reason' : 'Also'}</dt><dd>{r.reason}<span className="um-quiet"> · {shortDate(r.recorded_at)}</span></dd></React.Fragment>)}
            {k.note && <><dt>Note for Gwynn</dt><dd data-testid="correction-note">{k.note}</dd></>}
            {!k.note && k.note_withheld && <><dt>Note for Gwynn</dt><dd className="um-quiet" data-testid="correction-note">{CORRECTION_NOTE_WITHHELD}</dd></>}
          </dl>
          {manage && k.state !== 'COMPLETED' && (
            <div className="um-form" aria-label="Open correction">
              <label><span>Add to the reason (internal; never printed on the DCF fax cover)</span><input value={more} onChange={(e) => setMore(e.target.value)} maxLength={2000} aria-label="Additional reason" /></label>
              <div className="btns">
                <button type="button" className="um-open" disabled={busy || more.trim().length < 3} onClick={() => run(() => umedApi.addCorrectionReason(k.id, more.trim()), 'Reason added. The earlier reason is kept.')}>Add reason</button>
                {k.id === partialCorrectionId ? (
                  // PAR 03 (2026-10-08): a partial approval's correction completes only through APPROVED on the status chip,
                  // which also sets the case APPROVED; the server refuses the plain completion (PARTIAL_APPROVAL_CORRECTION).
                  <span className="um-quiet">This correction belongs to the partial approval. When FLMMIS shows the corrected months, choose APPROVED on the status chip.</span>
                ) : k.id === plCorrectionId ? (
                  // Verification 2, V2: an open PL correction's correction completes only through PL CORRECTION OUTCOME, which
                  // also resolves the PL correction; the server refuses the plain completion (PL_CORRECTION_OUTSIDE_ITS_ACT).
                  <span className="um-quiet">This correction belongs to the open PL correction. Record its outcome with PL CORRECTION OUTCOME on the status chip (Corrected, or Does not qualify).</span>
                ) : openSent ? (
                  <button type="button" className="um-open um-primary" disabled={busy} onClick={() => run(() => umedApi.completeCorrection(k.id), correctionCompletedWords)}>
                    {k.correction_type === 'PAID_IN_FULL_CORRECTION' ? 'Paid in full correction completed' : 'Correction completed'}
                  </button>
                ) : (
                  <span className="um-quiet" data-testid="correction-complete-unsent">
                    Not sent to DCF from eMerge yet: Gwynn sends it, then it can be completed once DCF shows it.{' '}
                    <label><input type="checkbox" checked={outside} disabled={busy} onChange={(e) => setOutside(e.target.checked)} /> {OUTSIDE_EMERGE_CONFIRM}</label>{' '}
                    <label><span>The day it went to DCF</span> <input type="date" value={outsideOn} max={todayLocal()} disabled={busy} onChange={(e) => setOutsideOn(e.target.value)} aria-label="The day the correction went to DCF outside eMerge" /></label>{' '}
                    <label><span>How it was sent</span> <input value={outsideHow} maxLength={OUTSIDE_NOTE_MAX} disabled={busy} onChange={(e) => setOutsideHow(e.target.value)} placeholder="Fax or upload, who sent it, and what shows it went" aria-label="How the correction was sent outside eMerge" /></label>{' '}
                    <button type="button" className="um-open" disabled={busy || !outsideReady} onClick={() => run(() => umedApi.completeCorrection(k.id, { sentOutsideEmerge: true, evidence: { sentOutsideOn: outsideOn, note: outsideHow.trim() } }), correctionCompletedWords)}>Complete it anyway</button>
                  </span>
                )}
              </div>
            </div>
          )}
          {manage && k.state !== 'COMPLETED' && row?.correction && row.correction.id === k.id && (
            <OpenCorrectionActs row={row} act={act} onAct={setAct} onDone={async (m) => { setAct(null); await onDone(m); }} />
          )}
        </div>
      ))}
      {/* 2026-10-21: no new correction while a DCF resubmittal is open (BoardTable.newCorrectionOffered, the row's rule). */}
      {manage && !open && resubmittalOpen && <p className="um-quiet" data-testid="correction-not-offered">{CORRECTION_RESUB_OPEN_NOTE}</p>}
      {manage && !open && !resubmittalOpen && (
        <div className="um-form" aria-label="Request a correction">
          <label><span>Correction type</span>
            <select value={type} onChange={(e) => setType(e.target.value as CorrectionType)} aria-label="Correction type">
              {types.map((t) => <option key={t} value={t}>{CORRECTION_TYPE_LABEL[t]}</option>)}
            </select>
          </label>
          <label><span>Reason (required)</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Correction reason" /></label>
          <label><span>{noteRequired ? 'Note for Gwynn (required)' : 'Instructions for Gwynn (optional)'}</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} aria-label={noteRequired ? 'Note for Gwynn' : 'Instructions for Gwynn'} /></label>
          <div className="btns"><button type="button" className="um-open um-primary" disabled={busy || !requestReady} aria-describedby={(!requestReady && firstReason([reason.trim().length < 3, 'Say why (at least 3 characters) first.'], [noteRequired && note.trim().length < 3, 'Write the note to Gwynn (at least 3 characters) first.'])) ? `why-correction-${caseId}` : undefined} onClick={() => run(() => umedApi.requestCorrection(caseId, { correction_type: type, reason: reason.trim(), ...(note.trim() ? { note_to_gwynn: note.trim() } : {}) }), 'Correction requested. Gwynn is told and the corrected UMED is prepared; it is on the Kanban under Corrections & resubmittals.')}>Request correction</button><Why id={`why-correction-${caseId}`} reason={!requestReady && firstReason([reason.trim().length < 3, 'Say why (at least 3 characters) first.'], [noteRequired && note.trim().length < 3, 'Write the note to Gwynn (at least 3 characters) first.'])} className="um-text um-why" /></div>
        </div>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * The native packet (workforce write cutover, D-XF-36). Gwynn checks what
 * the packet will say, states what eMerge holds no authority for (pages,
 * terms, total, comments), generates a version, opens it, and only then
 * records the sending. Generating never sends anything; a regeneration is a
 * new version and the old one is superseded.
 */
function PacketForm({ caseId, packets, onDone }: { caseId: string; packets: Packet[]; onDone: (msg: string) => Promise<void> }) {
  const current = packets.find((p) => p.state === 'VALID') || null;
  const [inputs, setInputs] = useState<PacketInputs>({});
  const [preview, setPreview] = useState<PacketPreview | null>(null);
  const [busy, setBusy] = useState<'preview' | 'generate' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof PacketInputs) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setInputs((prev) => ({ ...prev, [k]: e.target.value }));
  const check = async () => {
    setBusy('preview'); setErr(null);
    try { const r = await umedApi.previewPacket(caseId, inputs); if (!r.success || !r.data) { setErr(outcomeOf(r)); return; } setPreview(r.data); }
    catch { setErr('Could not reach the server.'); } finally { setBusy(null); }
  };
  const generate = async () => {
    setBusy('generate'); setErr(null);
    try {
      const r = await umedApi.generatePacket(caseId, inputs, freshKey());
      if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
      await onDone(`Packet v${r.data.version} generated. Nothing was sent: open it, check it, then record the sending.`);
    } catch { setErr('Could not reach the server. Nothing was generated.'); } finally { setBusy(null); }
  };
  return (
    <div className="um-form" aria-label="Generate the packet">
      <label><span>Pages (including cover)</span><input inputMode="numeric" value={inputs.pages ?? ''} onChange={set('pages')} aria-label="Packet pages" /></label>
      <label><span>Payment terms</span><input value={inputs.payment_terms ?? ''} onChange={set('payment_terms')} maxLength={200} aria-label="Packet payment terms" placeholder="Prefilled from the recorded terms when left blank" /></label>
      <label><span>Total ($)</span><input inputMode="decimal" value={inputs.total ?? ''} onChange={set('total')} aria-label="Packet total" placeholder="Prefilled from the recorded sale value when left blank" /></label>
      <label><span>Client case # (if not on file)</span><input value={inputs.client_case_number ?? ''} onChange={set('client_case_number')} maxLength={40} aria-label="Packet client case number" /></label>
      <label><span>Medicaid PIN (if known)</span><input value={inputs.client_medicaid_pin ?? ''} onChange={set('client_medicaid_pin')} maxLength={40} aria-label="Packet Medicaid PIN" /></label>
      <label><span>Comments</span><textarea value={inputs.comments ?? ''} onChange={set('comments')} maxLength={2000} rows={2} aria-label="Packet comments" /></label>
      <div className="btns">
        <button type="button" className="um-open" disabled={busy !== null} onClick={check}>{busy === 'preview' ? 'Checking…' : 'Check the packet'}</button>
        <button type="button" className="um-open um-primary" disabled={busy !== null || (preview !== null && !preview.validation.ok)} aria-describedby={(preview !== null && !preview.validation.ok && 'Fix what the preview lists first.') ? `why-packet-${caseId}` : undefined} onClick={generate}>
          {busy === 'generate' ? 'Generating…' : current ? `Regenerate (v${current.version + 1})` : 'Generate packet'}
        </button><Why id={`why-packet-${caseId}`} reason={preview !== null && !preview.validation.ok && 'Fix what the preview lists first.'} className="um-text um-why" />
      </div>
      {preview && (
        <div className="um-preview" aria-label="Packet preview">
          <p className="um-quiet">{PACKET_REASON_LABEL[preview.packet_reason]} · {preview.template_version} · next version {preview.next_version} · SSN {preview.ssn_on_file ? 'on file' : 'not on file'}</p>
          <dl className="um-kv">
            {Object.entries(preview.fields).filter(([k, v]) => v !== null && v !== '' && k !== 'ssn_on_file').map(([k, v]) => (
              <React.Fragment key={k}><dt>{PACKET_FIELD_LABEL[k] || k}</dt><dd>{k === 'total' ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : String(v)}</dd></React.Fragment>
            ))}
          </dl>
          {preview.validation.missing.length > 0 && <p className="um-notice stop" role="alert">Cannot generate yet: {preview.validation.missing.map((m) => PACKET_FIELD_LABEL[m] || m).join(' · ')}</p>}
          {preview.validation.warnings.length > 0 && <p className="um-quiet">Left blank on the packet: {preview.validation.warnings.map((w) => w.why).join(' ')}</p>}
        </div>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/** A generated packet version: open it (one audited short-lived URL), see its state. */
export function PacketRow({ caseId, packet, canReview = false, onDone }: { caseId: string; packet: Packet; canReview?: boolean; onDone?: (msg: string) => Promise<void> }) {
  const [err, setErr] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [complete, setComplete] = useState(false);
  const [warnings, setWarnings] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setErr(null);
    try {
      const r = await umedApi.documentUrl(caseId, packet.document_id);
      if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
      setOpened(true);
    } catch { setErr('Could not reach the server.'); }
  };
  return (
    <li>
      <span>v{packet.version} · {PACKET_REASON_LABEL[packet.packet_reason]} · {shortDate(packet.generated_at)}{packet.generated_by_name ? ` · ${packet.generated_by_name}` : ''} · {packet.state === 'VALID' ? packet.reviewed ? 'review recorded' : 'review required' : packet.state === 'SUBMITTED' ? 'sent' : 'superseded'}</span>
      {' '}<button type="button" className="um-link" onClick={open}>Open</button>
      {/* PR #416 round 3 (E): a revision carrying a document marked "Not this resident's" is never sent again. */}
      {packet.carries_misfiled && packet.carries_misfiled.length > 0 && (
        <p className="um-notice stop" role="note">Do not send: this revision carries a document marked “Not this resident's”. Opening it stays possible, for the record.</p>
      )}
      {canReview && packet.state === 'VALID' && !packet.reviewed && packet.review_supported && !(packet.carries_misfiled && packet.carries_misfiled.length > 0) && (
        <div role="group" aria-label={`Review manual packet v${packet.version}`}>
          {packet.validation.warnings.map((warning) => <p key={warning.field}>{warning.why}</p>)}
          <label><input type="checkbox" checked={complete} onChange={(event) => setComplete(event.target.checked)} />I reviewed this exact dated revision and acknowledge completeness.</label>
          <label><input type="checkbox" checked={warnings} onChange={(event) => setWarnings(event.target.checked)} />I acknowledge all listed warnings.</label>
          <button type="button" className="um-open" disabled={busy || !opened || !complete || !warnings} onClick={async () => {
            setBusy(true); setErr(null);
            try {
              const result = await umedApi.reviewPacket(caseId, { packet_id: packet.id, sha256: packet.sha256, complete: true, warnings_acknowledged: true });
              if (!result.success) { setErr(outcomeOf(result)); return; }
              await onDone?.('Exact manual packet revision reviewed.');
            } catch { setErr('Could not reach the server. No review was recorded.'); }
            finally { setBusy(false); }
          }}>Acknowledge complete manual packet</button>
        </div>
      )}
      {canReview && packet.state === 'VALID' && !packet.review_supported && <span> Regenerate this legacy revision before review and sending.</span>}
      {err && <span className="um-notice stop" role="alert"> {err}</span>}
    </li>
  );
}

/** A document eMerge holds for this case's resident: open it through one audited URL. */
/**
 * A UMED operator adds a file the case depends on (cutover 2026-09-21): DCF's
 * confirmation, the NOCA, a signed UMED, an audiogram. Same store and rules
 * as the Patient Advocate's upload; a UMED or audiogram added here releases
 * the board's "needs …" word the same way.
 */
function UmedUploadForm({ caseId, documentCount, onDone }: { caseId: string; documentCount: number; onDone: (msg: string) => Promise<void> }) {
  const [cls, setCls] = useState<UmedUploadClass>('DCF');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The same bytes are filed on another resident (2026-10-14): refused, and for the classes that may be shared, confirmable with a reason.
  const [shared, setShared] = useState<{ overridable: boolean } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  return (
    <form className="um-form" aria-label="Add a document" onSubmit={async (e) => {
      e.preventDefault(); if (!file) return; setBusy(true); setErr(null);
      try {
        const override = shared && shared.overridable ? overrideReason.trim() : '';
        if (shared && shared.overridable && override.length < 3) { setErr('Say why this file is shared (at least 3 characters).'); return; }
        const r = await umedApi.uploadDocument(caseId, cls, file, newActKey(), override || undefined);
        if (!r.success || !r.data) {
          if (r.code === 'DOCUMENT_BELONGS_TO_ANOTHER_RESIDENT') {
            const said = r.error && r.error !== 'Request failed' ? r.error : 'This exact file is already filed on another resident.';
            setShared({ overridable: r.detail && typeof r.detail.overridable === 'boolean' ? r.detail.overridable : !['UMED', 'AUDIOGRAM', 'NOCA'].includes(cls) });
            setErr(said); return;
          }
          setErr(outcomeOf(r)); return;
        }
        setFile(null); setShared(null); setOverrideReason('');
        await onDone(r.data.deduplicated ? 'That file was already on the record.' : `${UMED_UPLOAD_LABEL[cls]} added.`);
      } catch { setErr('Could not reach the server. Nothing was added.'); } finally { setBusy(false); }
    }}>
      <label><span>What it is</span>
        <select value={cls} onChange={(e) => { setCls(e.target.value as UmedUploadClass); setShared(null); setErr(null); }} aria-label="Document class">{UMED_UPLOAD_CLASSES.map((c) => <option key={c} value={c}>{UMED_UPLOAD_LABEL[c]}</option>)}</select>
      </label>
      <label><span>File</span><input key={`${caseId}:${documentCount}`} type="file" onChange={(e) => { setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null); setShared(null); setErr(null); }} aria-label="Document file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.docx,.doc,.msg,.eml" /></label>
      {shared && shared.overridable && (
        <label><span>Why is this file shared?</span><input type="text" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} maxLength={500} aria-label="Why this file is shared" /></label>
      )}
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !file} aria-describedby={(!file && 'Choose a file first.') ? `why-umed-upload-${caseId}` : undefined}>{busy ? 'Adding…' : shared && shared.overridable ? 'Upload anyway, it is shared' : 'Add document'}</button><Why id={`why-umed-upload-${caseId}`} reason={!file && 'Choose a file first.'} className="um-text um-why" /></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** The operators' notes on the case (cutover 2026-09-21): newest first, append-only, and a form when the caller may write one. */
function NotesSection({ caseId, notes, canWrite, onDone }: { caseId: string; notes: UmedNote[]; canWrite: boolean; onDone: (msg: string) => Promise<void> }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="um-section">
      <h3 className="rs-dup">Notes</h3>
      {notes.length === 0 ? <p className="um-quiet">No notes on this case yet.</p> : (
        <ul className="um-packets" aria-label="Case notes">
          {notes.map((n) => <li key={n.id}><span className="um-quiet">{shortDateTime(n.recorded_at)}{n.recorded_by_name ? ` · ${n.recorded_by_name}` : ''}</span><br />{n.note_withheld ? <span className="um-quiet" data-testid="note-withheld">{CORRECTION_NOTE_WITHHELD}</span> : n.note}</li>)}
        </ul>
      )}
      {canWrite && (
        <form className="um-form" aria-label="Add a note" onSubmit={async (e) => {
          e.preventDefault(); if (!text.trim()) return; setBusy(true); setErr(null);
          try {
            const r = await umedApi.addNote(caseId, text.trim(), newActKey());
            if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
            setText('');
            await onDone(r.data.replayed ? 'That note was already recorded.' : 'Note recorded.');
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>
          <label><span>Note</span><textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={8000} aria-label="Note" rows={3} /></label>
          <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || !text.trim()} aria-describedby={(!text.trim() && 'Write the note first.') ? `why-umed-note-${caseId}` : undefined}>{busy ? 'Recording…' : 'Add note'}</button><Why id={`why-umed-note-${caseId}`} reason={!text.trim() && 'Write the note first.'} className="um-text um-why" /></div>
          {err && <p className="um-notice stop" role="alert">{err}</p>}
        </form>
      )}
    </div>
  );
}

/**
 * One document, the way the Patient Advocate's drawer shows one (2026-10-02):
 * what kind it is, its name and where it came from, and a real Open button --
 * not an 11px underlined word at the end of a sentence. A 410 still says the
 * file is missing from storage rather than failing quietly.
 */
const DOC_KIND: Partial<Record<CaseDocument['document_class'], string>> = {
  NOCA: 'NOCA', UMED: 'UMED', AUDIOGRAM: 'Audio', DCF: 'DCF', FACE_SHEET: 'Face', ACH: 'ACH', RECORDING: 'Rec', BOM_VETTING: 'BOM', FLMMIS: 'FLMMIS',
};
// The classes a UMED operator files (umed.document.write), so the ones whose "Not this resident's" is theirs to say. A
// PA's recording, BOM vetting or face sheet is shown and opened here (ruling 2026-10-30, SCRUM-123) but marked by the PA.
const UMED_MARKABLE = ['UMED', 'AUDIOGRAM', 'DCF', 'NOCA', 'FLMMIS'];
function DocumentRow({ caseId, doc, canMark, onChanged }: { caseId: string; doc: CaseDocument; canMark?: boolean; onChanged?: (msg: string) => void | Promise<void> }) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Ruling 2026-10-30 (Isaiah Cotto, SCRUM-123): the PA's recordings play here, in the same player the PA uses.
  const player = useRecordingPlayer();
  const playable = isPlayable(doc);
  const link = async (): Promise<string | null> => {
    setErr(null); setBusy(true); player.retry();
    try {
      const r = await umedApi.documentUrl(caseId, doc.id);
      if (r.code === 'DOCUMENT_GONE' || r.status === 410) { player.gone(); setErr('eMerge has a record of this document, but the file is not in storage. Tell your manager; it was not deleted by you.'); return null; }
      if (!r.success || !r.data) { setErr(outcomeOf(r)); return null; }
      return r.data.url;
    } catch { setErr('Could not reach the server.'); return null; } finally { setBusy(false); }
  };
  const open = async () => { const u = await link(); if (u) window.open(u, '_blank', 'noopener,noreferrer'); };
  const play = async () => { const u = await link(); if (u) player.start(u); };
  const label = DOCUMENT_CLASS_LABEL[doc.document_class];
  const name = doc.original_filename || label;
  const meta = [
    label,
    doc.source_system === 'MONDAY' ? `from the board, ${shortDate(doc.source_created_at || doc.imported_at)}` : doc.source_system === 'DCF_MAIL' ? `mailed by DCF, ${shortDate(doc.created_at)}` : `added ${shortDate(doc.created_at)}`,
    doc.version > 1 ? `version ${doc.version}` : null,
    doc.filed_on === 'OTHER_CASE' || (doc.filed_on === undefined && doc.attached_to_this_case === false) ? 'Attached to another case for this resident' : null,
    doc.filed_on === 'RESIDENT' && doc.uploaded_by_pa ? 'Uploaded by the PA' : null,
    ...[...(doc.packets || [])].sort((a, b) => b.version - a.version).slice(0, 2).map((p) => (p.sent_on ? `In packet v${p.version} (sent ${mdy(p.sent_on)})` : `In packet v${p.version} (prepared)`)),
  ].filter(Boolean).join(' · ');
  return (
    <li className="um-doc">
      <span className="um-dockind" aria-hidden="true">{DOC_KIND[doc.document_class] || 'Doc'}</span>
      <span className="um-doc-body">
        <span className="um-doc-name" title={name} style={doc.misfiled ? { textDecoration: 'line-through' } : undefined}>{name}</span>
        <span className="um-doc-meta">{meta}</span>
        <MisfiledNote misfiled={doc.misfiled} refiled={doc.refiled} undone={doc.undone} tone="um" />
      </span>
      <span className="um-doc-acts">
        {canMark && !doc.misfiled && UMED_MARKABLE.includes(doc.document_class) && (
          <NotThisResidents tone="um"
            onSubmit={(b) => umedApi.markNotThisResidents(caseId, doc.id, { reason: b.reason, correct_patient_id: b.correctPatientId, idempotency_key: b.idempotencyKey })}
            search={async (q) => { const r = await umedApi.residentCandidates(caseId, q); return r.success && r.data ? r.data.items : []; }}
            onDone={(m) => { void (onChanged && onChanged(m || 'Marked not this resident\'s.')); }} />
        )}
        {playable && (player.media
          ? <button type="button" className="um-docbtn" onClick={player.close}>Close player</button>
          : <button type="button" className="um-docbtn um-primary" onClick={play} disabled={busy} aria-label={`Play ${name}`}>{busy ? 'Loading…' : 'Play'}</button>)}
        <button type="button" className="um-docbtn" onClick={open} disabled={busy} aria-label={`Open ${label}${doc.original_filename ? ` ${doc.original_filename}` : ''}`}>
          {busy && !playable ? 'Opening…' : 'Open'}
        </button>
      </span>
      <RecordingAudio player={player} name={name} tone="um" />
      {err && <p className="um-notice stop um-doc-err" role="alert">{err}</p>}
    </li>
  );
}

/**
 * The packet went to DCF (packet §24). Gwynn records how it went and any
 * reference, citing the packet version sent; the case leaves Needs creation
 * by derivation and the ten-day review opens. No group is moved, no board is
 * written. Without a generated packet there is nothing to record.
 */
function SubmissionForm({ caseId, scope, packetId, onDone }: { caseId: string; scope: Scope; packetId: string | null; onDone: (msg: string) => Promise<void> }) {
  const methods = scope.submission_methods || (['FAX', 'PORTAL_UPLOAD', 'MAIL', 'OTHER'] as SubmissionMethod[]);
  const [method, setMethod] = useState<SubmissionMethod>(methods[0]);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const keyRef = useRef<string>(freshKey());
  if (!packetId) return <p className="um-quiet">Generate the packet before recording the sending.</p>;
  return (
    <form className="um-form" aria-label="Record the sending" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await umedApi.recordSubmission(caseId, { method, reference: reference.trim() || undefined, packet_id: packetId, idempotency_key: keyRef.current });
        if (!r.success || !r.data) { setErr(outcomeOf(r)); return; }
        keyRef.current = freshKey();
        await onDone(r.data.replayed ? 'That sending was already recorded.' : r.data.approved ? 'Sending recorded. The case stays approved in eMerge; no approval review opens.' : `Sending recorded. This case leaves Needs creation; the approval review is due ${r.data.reviewDueOn ? shortDate(r.data.reviewDueOn) : 'in ten days'}.`);
      } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <label><span>How it went</span>
        <select value={method} onChange={(e) => setMethod(e.target.value as SubmissionMethod)} aria-label="Submission method">
          {methods.map((m) => <option key={m} value={m}>{SUBMISSION_METHOD_LABEL[m]}</option>)}
        </select>
      </label>
      <label><span>Reference (optional)</span><input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} aria-label="Submission reference" /></label>
      <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Recording…' : 'Sent to DCF'}</button></div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * DCF resubmittals (U-18, packet §29–30; Isaiah Cotto 2026-10-15: "Reversals are really resubmittals"). Finance starts
 * one, states its kind and correction and hands it to UMED; UMED prepares and faxes the packet (its sending advances
 * the resubmittal), records DCF's answer, sends again after a decline, and may withdraw one handed to it BEFORE anything
 * was sent. Since 2026-10-19 UMED starts one too, from the board row's status menu (REVERSAL / DCF RESUBMITTAL…, BoardTable), and
 * it is handed to UMED in the same act; this panel works it like any other. Nothing here erases the original UMED or
 * anything Finance did. Every act decides again on the server.
 */
function ReversalsPanel({ reversals, scope, onDone, approvedByAct = false }: {
  caseId: string; reversals: Reversal[]; scope: Scope; onDone: (msg: string) => Promise<void>;
  /** The case carries the approval act: an APPROVED stage before the resubmittal is then "Approved", not Monday's claim (P3). */
  approvedByAct?: boolean;
}) {
  const [more, setMore] = useState('');
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const manage = canDo(scope, 'manage_reversals');
  // PR #572 review: the success line also carries the server's words when ruling B blocked a second Finance handoff.
  const run = async (fn: () => Promise<{ success: boolean; error?: string; message?: string; code?: string; data?: unknown }>, msg: string, blockedMsg: string | null = null) => {
    setBusy(true); setErr(null);
    try { const r = await fn(); if (!r.success) { setErr(outcomeOf(r)); return; } setMore(''); setWhy(''); setWithdrawing(null); const blocked = blockedWords(r); await onDone(blocked ? `${blockedMsg || msg}${blocked}` : msg); }
    catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  if (reversals.length === 0) return null;
  return (
    <div className="um-section">
      <h3>Reversals Submitted</h3>
      {reversals.map((v) => {
        const handed = Boolean(v.handed_to_umed_at);
        const withFinance = v.state === 'REQUESTED' && !handed;
        const open = v.state !== 'COMPLETED' && v.state !== 'WITHDRAWN';
        const terms = v.kind === 'CORRECTED' ? termsLine(v.corrected_terms) : null;
        const total = v.kind === 'CORRECTED' && v.corrected_terms ? dollars(v.corrected_terms.total_cents) : null;
        // The packet's own sending normally advances it; this records one made outside eMerge. Only a handed row with a kind
        // (or a legacy row already sent) may be sent: a row with Finance has no packet.
        const canSend = (v.state === 'REQUESTED' && handed && Boolean(v.kind)) || (v.state === 'NOT_APPROVED' && (Boolean(v.kind) || v.submission_count > 0));
        const canWithdraw = v.state === 'REQUESTED' && handed && v.submission_count === 0;
        return (
          <div key={v.id} className="um-kv-block">
            <dl className="um-kv">
              <dt>{reversalStateWords(v.state, handed)}</dt>
              <dd>requested {shortDate(v.requested_at)}{v.requested_by_name ? ` by ${v.requested_by_name}` : ''} · asked for by {REVERSAL_REQUESTER_LABEL[v.requester].toLowerCase()}</dd>
              <dt>Kind</dt>
              <dd>{v.kind ? KIND_LABEL[v.kind] : `${KIND_NOT_STATED} -- with Finance`}</dd>
              {v.kind === 'CORRECTED' && <><dt>Corrected terms</dt><dd>{terms || 'not stated'}</dd><dt>Corrected total</dt><dd>{total || 'not stated'}</dd></>}
              {v.kind === 'FULL' && <><dt>Effect</dt><dd>The claim is unwound.</dd></>}
              <dt>Before</dt>
              <dd>{stageLabelOf(v.prior_state.stage, approvedByAct)} · NOCA {NOCA_LABEL[v.prior_state.noca_status]} · FLMMIS {FLMMIS_LABEL[v.prior_state.flmmis_status]} · Finance {FINANCE_STATE_LABEL[v.prior_state.finance_state] || String(v.prior_state.finance_state).toLowerCase()}</dd>
              {handed && <><dt>Handed to UMED</dt><dd>{shortDate(v.handed_to_umed_at as string)}{v.handed_by_name ? ` by ${v.handed_by_name}` : ''}</dd></>}
              {v.submission_count > 0 && <><dt>Sent to DCF</dt><dd>{v.submission_count === 1 ? 'once' : `${v.submission_count} times`}{v.last_submitted_at ? `, last ${shortDate(v.last_submitted_at)}` : ''}</dd></>}
              {v.outcome && <><dt>DCF said</dt><dd>{v.outcome === 'APPROVED' ? 'Approved' : 'Not approved'}{v.outcome_recorded_at ? ` · ${shortDate(v.outcome_recorded_at)}` : ''}</dd></>}
              {v.state === 'WITHDRAWN' && <><dt>Withdrawn</dt><dd>{v.withdrawn_at ? shortDate(v.withdrawn_at) : 'date not recorded'}{v.withdrawn_by_name ? ` by ${v.withdrawn_by_name}` : ''}{v.withdrawn_reason ? `: ${v.withdrawn_reason}` : ''}</dd></>}
              {v.reasons.map((r, i) => <React.Fragment key={r.id}><dt>{i === 0 ? 'Reason' : 'Also'}</dt><dd>{r.reason}<span className="um-quiet"> · {shortDate(r.recorded_at)}</span></dd></React.Fragment>)}
            </dl>
            {withFinance && <p className="um-quiet">With Finance -- Finance hands it to UMED.</p>}
            {manage && open && !withFinance && (
              <div className="um-form" aria-label="Open DCF resubmittal">
                <label><span>Add to the reason (internal; never printed on the DCF packet)</span><input value={more} onChange={(e) => setMore(e.target.value)} maxLength={2000} aria-label="Additional resubmittal reason" /></label>
                <div className="btns">
                  <button type="button" className="um-open" disabled={busy || more.trim().length < 3} onClick={() => run(() => umedApi.addReversalReason(v.id, more.trim()), 'Reason added. The earlier reason is kept.')}>Add reason</button>
                  {canSend && (
                    <button type="button" className="um-open um-primary" disabled={busy} onClick={() => run(() => umedApi.reversalSubmitted(v.id), v.state === 'NOT_APPROVED' ? "DCF resubmittal sent to DCF again. Awaiting DCF's answer." : "DCF resubmittal sent to DCF. Awaiting DCF's answer.")}>
                      {v.state === 'NOT_APPROVED' ? 'Sent to DCF again' : 'Sent to DCF'}
                    </button>
                  )}
                  {v.state === 'SUBMITTED' && (
                    <>
                      <button type="button" className="um-open um-primary" disabled={busy} onClick={() => run(() => umedApi.reversalOutcome(v.id, 'APPROVED'), v.kind === 'FULL' ? 'DCF approved the resubmittal: the claim is unwound. If Finance had anything to unwind, the handoff is open.' : 'DCF approved the resubmittal: the claim stands at the corrected terms.', v.kind === 'FULL' ? 'DCF approved the resubmittal: the claim is unwound.' : 'DCF approved the resubmittal: the claim stands at the corrected terms.')}>DCF approved</button>
                      <button type="button" className="um-open" disabled={busy} onClick={() => run(() => umedApi.reversalOutcome(v.id, 'NOT_APPROVED'), 'Recorded: DCF did not approve the resubmittal. Decide whether to send it again.')}>DCF did not approve</button>
                    </>
                  )}
                  {canWithdraw && withdrawing !== v.id && (
                    <button type="button" className="um-open" disabled={busy} onClick={() => { setWithdrawing(v.id); setWhy(''); setErr(null); }}>Withdraw</button>
                  )}
                </div>
                {canWithdraw && withdrawing === v.id && (
                  <div className="um-form" aria-label="Withdraw the DCF resubmittal">
                    <label><span>Why it is withdrawn (required)</span><textarea value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000} aria-label="Withdrawal reason" /></label>
                    <div className="btns">
                      <button type="button" className="um-open um-primary" disabled={busy || why.trim().length < 3} aria-describedby={(why.trim().length < 3 && 'Say why (at least 3 characters) first.') ? `why-withdraw-${v.id}` : undefined} onClick={() => run(() => umedApi.withdrawResubmittal(v.id, why.trim()), 'DCF resubmittal withdrawn. Nothing had been sent to DCF.')}>Withdraw the resubmittal</button><Why id={`why-withdraw-${v.id}`} reason={why.trim().length < 3 && 'Say why (at least 3 characters) first.'} className="um-text um-why" />
                      <button type="button" className="um-open" disabled={busy} onClick={() => { setWithdrawing(null); setWhy(''); }}>Keep it</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * EP-005: take an unowned case a Patient Advocate handed over. Offered only
 * when the server says the case is claimable and this person may claim it.
 */
function ClaimCase({ caseId, onChanged }: { caseId: string; onChanged: (msg: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const take = async () => {
    setBusy(true); setErr(null);
    const r = await umedApi.claimCase(caseId);
    setBusy(false);
    if (r.success) { await onChanged('This case is yours now. Its creation work is in your My work.'); return; }
    setErr(r.code === 'ALREADY_CLAIMED'
      ? 'Another operator took this case a moment ago. Refresh to see who.'
      : (r.message || r.error || 'The case could not be taken.'));
  };
  return (
    <div className="btns">
      <button type="button" className="um-open um-primary" disabled={busy} onClick={take}>Take this case</button>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </div>
  );
}

/**
 * CONFIRM_ENROLLMENT — the VA's act (EP-010 §8).
 *
 * The business's words: "our VAs confirm the patient's enrolment and should
 * update the patient's record in the UMED portal to enrolled." That is a
 * HUMAN confirmation, and it is deliberately not the same fact as the derived
 * one: an approval (the operator's Approve act, 2026-10-06) already enrols a resident
 * without anybody clicking, and the sale records which of the two it was.
 *
 * What this cannot do, and says so: it does not write an approval. Confirming
 * that a resident enrolled is evidence about enrolment and never about DCF,
 * so nothing here moves the UMED to APPROVED or makes the workspace say
 * "Approved". The one sentence that must never be wrong stays true.
 *
 * It also never creates a sale. Enrolment is a state ON the existing one, so
 * a resident whose PA never marked the case ready to submit has nothing to
 * enrol, and the server says exactly that rather than inventing a sale.
 */
function EnrollmentSection({ caseId, onDone }: { caseId: string; onDone: (msg: string) => Promise<void> }) {
  const [state, setState] = useState<'ENROLLED' | 'PENDING_CONFIRMATION' | 'NOT_ENROLLED_FINAL'>('ENROLLED');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const needsReason = state === 'NOT_ENROLLED_FINAL';
  return (
    <div className="um-section">
      <h3>Enrollment</h3>
      <form className="um-form" aria-label="Confirm the resident's enrollment" onSubmit={async (e) => {
        e.preventDefault(); setBusy(true); setErr(null);
        try {
          const r = await umedApi.confirmEnrollment(caseId, { state, reason: reason.trim() || null, idempotency_key: freshKey() });
          if (!r.success || !r.data) {
            setErr(r.code === 'NO_SALE'
              ? 'This resident has no recorded sale, so there is nothing to mark enrolled. A sale is recorded when the Patient Advocate marks the case ready to submit.'
              : (r.error || r.message || 'That did not go through.'));
            return;
          }
          setReason('');
          await onDone(!r.data.changed
            ? 'The enrollment already read that.'
            : state === 'ENROLLED'
              ? `Enrollment confirmed. It shows on the advocate's Monthly sales; the sale, its amount and its credit are unchanged.`
              : 'Recorded. The sale stays where it is; nothing about it was erased.');
        } catch { setErr('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
      }}>
        <label><span>The resident</span>
          <select value={state} onChange={(e) => setState(e.target.value as typeof state)} aria-label="Enrollment outcome">
            <option value="ENROLLED">Enrolled</option>
            <option value="PENDING_CONFIRMATION">Still confirming</option>
            <option value="NOT_ENROLLED_FINAL">Did not enrol</option>
          </select>
        </label>
        <label><span>{needsReason ? 'Why (required)' : 'Note (optional)'}</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Enrollment reason" />
        </label>
        <p className="um-quiet">
          This records enrollment only. It does not write an approval, and it never creates a second sale — the advocate&rsquo;s
          credit, the amount and the close date are untouched.
        </p>
        <div className="btns"><button type="submit" className="um-open um-primary" disabled={busy || (needsReason && !reason.trim())} aria-describedby={(needsReason && !reason.trim() && 'Say why first.') ? `why-enrollment-${caseId}` : undefined}>{busy ? 'Recording…' : 'Record enrollment'}</button><Why id={`why-enrollment-${caseId}`} reason={needsReason && !reason.trim() && 'Say why first.'} className="um-text um-why" /></div>
        {err && <p className="um-notice stop" role="alert">{err}</p>}
      </form>
    </div>
  );
}

/**
 * The terms, editable from the case itself -- approved cases included.
 *
 * Josie Evans, 2026-10-06: "This patient was an approval today, but when I
 * click it open there is nowhere for me to update/change the terms if needed."
 *
 * The same act as the row's "Change" (POST /terms, umed_case_terms): one box,
 * the line as she types it, what eMerge reads from it echoed before it is
 * recorded. Append-only, so every change is a new version and the panel lists
 * them all, newest first, with who and when. The newest is what the board row,
 * the packet and anything downstream read; the closed lead total (the sale) is
 * never touched.
 */
function TermsPanel({ caseId, terms, canWrite, onDone }: {
  caseId: string;
  terms: CaseDetail['terms'];
  canWrite: boolean;
  onDone: (msg: string) => Promise<void>;
}) {
  const current = terms?.current || null;
  const history = terms?.history || [];
  const [editing, setEditing] = useState(false);
  // Add the original terms (2026-10-21): offered only while the case has none in eMerge.
  const [adding, setAdding] = useState(false);
  useEffect(() => { setAdding(false); }, [caseId, current?.id]);
  const [line, setLine] = useState(current?.terms_text || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { setEditing(false); setErr(null); setLine(current?.terms_text || ''); }, [caseId, current?.id, current?.terms_text]);
  /*
   * Focus without scrolling (design verification P2-3): the panel sits in a
   * detail cell as wide as the table, and autoFocus scrolled the table sideways
   * until the editor was clipped. Cancel and Escape give focus back to the
   * button that opened it, never to the page body (P2-1).
   */
  const input = React.useRef<HTMLInputElement | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const refocus = React.useRef(false);
  useEffect(() => {
    if (editing) input.current?.focus({ preventScroll: true });
    else if (refocus.current) { refocus.current = false; opener.current?.focus({ preventScroll: true }); }
  }, [editing]);
  const cancel = () => { refocus.current = true; setEditing(false); };

  const typed = line.trim();
  const read = parseTermsLine(typed);
  const money = (c: number | null) => (c === null ? null : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c / 100));
  const said = (t: NonNullable<typeof current>) => t.terms_text
    || (t.months !== null ? `${t.months} ${t.months === 1 ? 'month' : 'months'}${t.monthly_cents !== null ? ` at ${money(t.monthly_cents)}` : ''}` : 'Recorded without a line');
  const shown = showAll ? history : history.slice(0, 5);

  return (
    <div className="um-section um-terms" data-case-anchor="terms">
      <h3>Terms</h3>
      {current
        ? <p className="um-terms-current"><strong>{said(current)}</strong><span className="um-quiet">{current.original ? ' · original terms' : ''} · {current.recorded_by_name || 'eMerge'} · {shortDateTime(current.recorded_at)}</span></p>
        : <p className="um-quiet">No terms recorded in eMerge yet.{terms?.original_needed ? ' The packet has no terms or total to print: add the original terms.' : ''}</p>}
      {/* Only where the packet would otherwise lack its terms or total; a case whose sale gives them is not overridden. */}
      {canWrite && !current && terms?.original_needed === true && !editing && !adding && (
        <button type="button" className="um-open um-primary" onClick={() => { setErr(null); setAdding(true); }}>Add the original terms</button>
      )}
      {canWrite && !current && terms?.original_needed === true && adding && (
        <OriginalTermsForm caseId={caseId} boardText={terms?.board_text || null}
          onCancel={() => setAdding(false)} onDone={async (msg) => { setAdding(false); await onDone(msg); }} />
      )}
      {canWrite && !editing && !adding && (
        <button type="button" className="um-open" ref={opener} onClick={() => { setErr(null); setLine(current?.terms_text || ''); setEditing(true); }}>
          {current ? 'Change terms' : 'Record terms'}
        </button>
      )}
      {canWrite && editing && (
        <form className="um-form" aria-label="Change the terms" onSubmit={async (e) => {
          e.preventDefault();
          if (!typed || typed.length > 200) return;
          setBusy(true); setErr(null);
          try {
            const r = await umedApi.recordTerms(caseId, { terms_text: typed, idempotency_key: freshKey() });
            if (!r.success) { setErr(outcomeOf(r)); return; }
            setEditing(false);
            await onDone('Terms recorded as a new version. The earlier ones are kept below.');
          } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
        }}>
          <label><span>Terms</span>
            <input value={line} maxLength={200} disabled={busy} ref={input} aria-label="Terms"
              placeholder="10/1/26-12/1/26 (3 months at $1,932.33 = $5,797)"
              onChange={(e) => setLine(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }} />
          </label>
          {typed && (read.parsed
            ? <p className="um-quiet" aria-live="polite">Reads as {read.months} {read.months === 1 ? 'month' : 'months'} at {money(read.monthly_cents)}{read.total_cents !== null ? ` = ${money(read.total_cents)}` : ''}.</p>
            : <p className="um-quiet" aria-live="polite">Kept exactly as you typed it{read.months_only !== null ? `; eMerge reads ${read.months_only} months and works the monthly amount out from the closed lead total` : ', with no figures read from it'}.</p>)}
          <p className="um-quiet">A new version beside the old one: nothing is overwritten, and the closed lead total stays as it was.</p>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !typed || typed.length > 200} aria-describedby={(firstReason([!typed, 'Enter the terms first.'], [typed.length > 200, 'Keep the terms to 200 characters.'])) ? `why-terms-${caseId}` : undefined}>{busy ? 'Recording…' : 'Record terms'}</button><Why id={`why-terms-${caseId}`} reason={firstReason([!typed, 'Enter the terms first.'], [typed.length > 200, 'Keep the terms to 200 characters.'])} className="um-text um-why" />
            <button type="button" className="um-link" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
          {err && <p className="um-notice stop" role="alert">{err}</p>}
        </form>
      )}
      {history.length > 1 && (
        <>
          <h4 className="um-terms-h">Every version</h4>
          <ol className="um-story" aria-label="Terms history">
            {shown.map((t, i) => (
              <li key={t.id}>
                <span className="when">{shortDate(t.recorded_at)}</span>
                <span className="what"><strong>{said(t)}</strong><span>{t.original ? ' · original terms' : ''} · {t.recorded_by_name || 'eMerge'}{i === 0 ? ' · current' : ''}{t.note ? ` · ${t.note}` : ''}</span></span>
              </li>
            ))}
          </ol>
          {history.length > 5 && (
            <button type="button" className="um-link" aria-expanded={showAll} onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show the latest five' : `Show all ${history.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** The open record's sections, in order (2026-10-30). */
export type CaseSectionKey = 'overview' | 'dcf' | 'approval' | 'documents' | 'notes' | 'terms' | 'history';
/** Where an act chosen on the board lands: its section opens (caseAnchor.ts scrolls to the anchor inside it). */
const CASE_SECTION_OF_FOCUS: Partial<Record<CaseFocus['kind'], CaseSectionKey>> = {
  packet: 'dcf', 'confirm-upload': 'dcf', 'fax-dcf': 'dcf', 'record-fax': 'dcf', 'choose-sending': 'dcf', 'dcf-email': 'dcf',
  'approval-evidence': 'approval', approve: 'approval', review: 'approval', 'approval-email': 'approval',
  terms: 'terms',
};
/**
 * Whether a section of the case is open: a board act's section is, until the person folds it themselves after that
 * act (`released` is the act they folded it after); otherwise their own choice for this case; otherwise the default.
 */
export function caseSectionIsOpen(k: CaseSectionKey, o: { focus: CaseFocus | null; released: string; chosen: Partial<Record<CaseSectionKey, boolean>>; byDefault: Record<CaseSectionKey, boolean> }): boolean {
  const focusKey = o.focus ? `${o.focus.kind}:${o.focus.n}` : '';
  if (o.focus && CASE_SECTION_OF_FOCUS[o.focus.kind] === k && o.released !== focusKey) return true;
  const v = o.chosen[k];
  return typeof v === 'boolean' ? v : o.byDefault[k];
}

/*
 * "Who to bill / contact" (SCRUM-132, 2026-11-03; the ruling's default): during review, in the Approval section, under the
 * gate the approval detail is under -- the server sends `billing_contact` only to a caller with approval detail whose
 * approval scope holds this building, and null to anyone else, so nothing is drawn then. Read-only: the POA (eMerge holds
 * no POA address), the responsible party with its address -- where invoices go -- and where that came from, and who
 * manages the funds. Each fact said, or "not recorded"; nothing is inferred.
 */
const RP_SOURCE_LABEL: Record<string, string> = { EMERGE: 'recorded in eMerge', MONDAY: 'from the Monday board', COUNSELEAR: 'from CounselEAR' };
export function BillingContactBlock({ contact }: { contact: CaseDetail['billing_contact'] }) {
  if (!contact) return null;
  const nr = <span className="um-quiet">not recorded</span>;
  const line = (...parts: Array<string | null>) => (parts.some(Boolean) ? parts.filter(Boolean).join(' · ') : null);
  const { poa, responsible_party: rp, funds_manager: fm } = contact;
  const funds = fm.type ? (FINANCIAL_RESPONSIBILITY_LABEL[fm.type as FinancialResponsibilityType] || fm.type) : null;
  return (
    <div className="um-section" data-testid="billing-contact" aria-label="Who to bill / contact">
      <h3>Who to bill / contact</h3>
      <dl className="um-kv">
        <dt>Funds managed by</dt><dd>{funds ? `${funds}${fm.source === 'MONDAY' ? ' (from the Monday board)' : ''}` : nr}</dd>
        <dt>POA</dt><dd>{line(poa.name, poa.email, poa.phone) || nr}</dd>
        <dt>Responsible party</dt><dd>{line(rp.name, rp.email, rp.phone) || nr}{rp.source ? <span className="um-quiet"> · {RP_SOURCE_LABEL[rp.source] || rp.source}</span> : null}</dd>
        <dt>Send invoices to</dt><dd data-testid="billing-address">{rp.address || nr}</dd>
      </dl>
    </div>
  );
}

function CaseContext({ detail, refusal, loading, scope, notice, onChanged, focus = null, statusActs = false, onFocus }: {
  detail: CaseDetail | null; refusal: Refusal | null; loading: boolean; scope: Scope; notice: string | null; onChanged: (msg: string) => Promise<void>;
  /** Where an act chosen on the board continues here (Gwynn, 2026-10-12): Confirm DCF upload, or the DCF account form. */
  focus?: CaseFocus | null;
  /**
   * 2026-11-03 (SCRUM-127/129): draw the case's status acts here (DockStatus: the status menu and Set status), where the
   * record is opened from the Kanban or a list. The Table's row carries its own, beside the record, so it passes false.
   */
  statusActs?: boolean;
  /** An act chosen in the record's status acts that continues in a section of the record (the packet, the terms...). */
  onFocus?: (kind: CaseFocus['kind']) => void;
}) {
  const [showProvenance, setShowProvenance] = useState(false);
  /* An act whose control disappears on success (Approve, Mark submitted) hands focus to the notice that says what happened (P1-b). */
  const noticeRef = React.useRef<HTMLParagraphElement | null>(null);
  const actDone = (m: string) => { void onChanged(m).then(() => noticeRef.current?.focus({ preventScroll: false })); };
  /* Approved here: the approval email opens next (2026-10-13), for that case only. */
  const [emailAsk, setEmailAsk] = useState<{ caseId: string | null; n: number }>({ caseId: null, n: 0 });
  /* UMED SUBMITTED TO DCF: the chooser's pick, for the case and the ask it was made on (a fresh number per pick). */
  const [chosen, setChosen] = useState<{ key: string; kind: 'confirm-upload' | 'fax-dcf' | 'record-fax'; n: number } | null>(null);
  const chosenSeq = React.useRef(1000000);
  const askKey = focus ? `${detail?.case.id || ''}:${focus.kind}:${focus.n}` : '';
  // A move refused or opened on the Board layout lands on its section (2026-10-13): the packet, or the approval evidence.
  useCaseAnchor(focus, detail?.case.id || null);
  // Which sections of the record are open (2026-10-30), per case: a new case starts from the defaults. A board act that
  // lands in a section (CASE_SECTION_OF_FOCUS) opens it until the person folds it themselves.
  const [folds, setFolds] = useState<{ caseId: string | null; release: string; map: Partial<Record<CaseSectionKey, boolean>> }>({ caseId: null, release: '', map: {} });
  if (loading && !detail) return <Skeleton announce={false} />;
  if (refusal) return <section className="um-card"><StateBlock refusal={refusal} /></section>;
  if (!detail) return <section className="um-card"><p className="um-empty">Select a case to see the next action, the approval evidence and what already happened.</p></section>;

  const c = detail.case;
  const next = dueWords(c.next_action);
  const done = detail.actions.filter((a) => a.status !== 'OPEN');
  const readiness = c.readiness;
  const account: DcfAccountRecord = detail.dcf_account || { state: c.dcf_account?.state || 'UNKNOWN', email: null, unable_reason: null, has_credential: false, source: 'MONDAY' };
  const writes = scope.writes === 'NATIVE';
  const approvedHere = c.approval?.canonical === 'PROVEN';
  /** Approved in eMerge and the sending would be a plain one (NEW, or unnamed): the server refuses it (P2-B), so it is not offered. */
  // A preparation withdrawn ONLY because the board still moves the case (NOT_NATIVE) is not live: for a manual packet the
  // server forces forward_authority EMERGE before it asks whether the episode is closed (preparation.js:708-709, used by
  // reviewPacket :721 and assertReviewedForSubmission :819), so NOT_NATIVE (prepInputs.js:213) is the one closure it waives.
  // Every other withdrawal reason (case closed, resident withdrawn, PA withdrew readiness, past submission, ...) still refuses
  // review, sending and a new generate with 409, so those keep the manual controls hidden. The reason is the stored
  // withdrawn_reason of the preparation row (readModel.js PACKET_PREPARATION); the server still decides.
  const livePrep = Boolean(c.packet_preparation) && !(c.packet_preparation?.state === 'WITHDRAWN' && c.packet_preparation?.withdrawn_reason === 'NOT_NATIVE');
  const plainSendingClosed = approvedHere && Boolean(readiness) && (readiness?.purpose === 'NEW' || readiness?.purpose === 'UNKNOWN');
  /**
   * An open reversal or correction is what is being sent (final verification P2-1): the server derives the
   * sending's and the packet's purpose from it, so its packet is generated and its sending recorded here --
   * for that purpose only -- on an approved case too.
   */
  const flowSending = readiness?.purpose_from === 'REVERSAL' || readiness?.purpose_from === 'CORRECTION';
  /** Readiness on an approved case only while a sending it allows (a correction's, a reversal's) is still to be made (P3). */
  const showReadiness = Boolean(readiness) && (!approvedHere || (!plainSendingClosed && !c.submission_recorded));
  /** The packet a sending cites is one generated for what is being sent now; an older purpose's packet is not offered. */
  const sendablePacketId = (detail.packets || []).find((p) => p.state === 'VALID' && p.reviewed === true && (!readiness || p.purpose === readiness.purpose))?.id || null;
  /* The sections (2026-10-30): open by default where there is something to read or do; an empty one folds to a quiet line. */
  const docItems = detail.documents.items.filter((d) => d.document_class !== 'UMED_GENERATED_PACKET');
  const docsShown = detail.documents.available || Boolean(detail.documents.why);
  const docCount = detail.documents.available ? docItems.length : null;
  const notesCount = detail.notes ? detail.notes.length : 0;
  const termsHeld = Boolean(detail.terms?.current) || (detail.terms?.history?.length || 0) > 0;
  const financeShown = Boolean(scope.finance_visible && c.finance);
  const sectionDefault: Record<CaseSectionKey, boolean> = {
    overview: true, dcf: true, approval: true,
    documents: Boolean(docCount), notes: notesCount > 0,
    terms: termsHeld || financeShown || detail.terms?.original_needed === true,
    history: false,
  };
  const focusKey = focus ? `${focus.kind}:${focus.n}` : '';
  const forced = focus ? CASE_SECTION_OF_FOCUS[focus.kind] : undefined;
  const foldMap = folds.caseId === c.id ? folds.map : {};
  const released = folds.caseId === c.id ? folds.release : '';
  const sectionOpen = (k: CaseSectionKey): boolean => caseSectionIsOpen(k, { focus, released, chosen: foldMap, byDefault: sectionDefault });
  const setSection = (k: CaseSectionKey) => (open: boolean) => setFolds((prev) => {
    const same = prev.caseId === c.id;
    return { caseId: c.id, release: forced === k ? focusKey : (same ? prev.release : ''), map: { ...(same ? prev.map : {}), [k]: open } };
  });
  const sid = (k: CaseSectionKey) => `um-case-${c.id}-${k}`;

  return (
    <section className="um-card um-case" aria-label="Case context">
      <div className="um-card-head">
        <div>
          <h2>{personName(c)}</h2>
          <p className="why"><BuildingOrClinic row={c} fallback="Facility not recorded" /> · {workType(c)} · {stateLine(c)}</p>
          {financeLine(c) && <p className="why">{financeLine(c)}</p>}
          {provenanceLine(c) && <p className="um-prov">{provenanceLine(c)}</p>}
        </div>
        <span className="um-tags">
          <DispositionChip disposition={c.disposition} />
          <PaidInFullChip mark={c.paid_in_full_mark} />
          <AttentionTags items={c.attention} superseded={Boolean(c.effective_state?.provenance?.superseded_exception)} />
        </span>
      </div>

      {notice && <p className="um-notice" role="status" tabIndex={-1} ref={noticeRef}>{notice}</p>}

      {/* What matters next (2026-10-30): the next action, its owner and due date, and what is in the way -- first, before any section. */}
      <div className="um-nextstrip" data-testid="case-next">
        <div className="um-nextstrip__now">
          <h3>Current work</h3>
          {c.next_action ? (
            <>
              <p className="um-next">{ACTION_LABEL[c.next_action.action_type]}</p>
              <dl className="um-kv">
                <dt>Owner</dt><dd><OwnerLine owner={c.owner} claimable={Boolean(c.claimable)} /></dd>
                <dt>Due</dt><dd className={next.stop ? 'stop' : ''}>{next.text}</dd>
                {c.creation_reason && <><dt>Reason</dt><dd>{CREATION_REASON_LABEL[c.creation_reason]}</dd></>}
                {c.next_action.reason && <><dt>Why</dt><dd>{c.next_action.reason}</dd></>}
              </dl>
              {c.claimable && canDo(scope, 'claim_case') && <ClaimCase caseId={c.id} onChanged={onChanged} />}
            </>
          ) : (
            <>
              <p className="um-next none">No open obligation</p>
              <p className="um-quiet">Nothing in the source proves work outstanding on this case. That is not the same as finished — it is that no rule can name a next action from what the board carries.</p>
            </>
          )}
        </div>
        {c.readiness_blockers.length > 0 && (
          <div className="um-nextstrip__way">
            <h3>In the way</h3>
            <ul className="um-blockers">{c.readiness_blockers.map((b) => <li key={b}>{BLOCKER_LABEL[b] || b}</li>)}</ul>
          </div>
        )}
      </div>

      {/* 2026-11-03: Set status and the status menu, reachable from the Kanban (the default layout), on the docked record. */}
      {statusActs && <DockStatus row={c} scope={scope} onChanged={onChanged} onContinue={onFocus} onFocusNotice={() => (noticeRef.current?.isConnected ? noticeRef.current : document.querySelector<HTMLElement>('.um-case .um-notice[role="status"]'))?.focus({ preventScroll: false })} />}

      {/*
        The record in sections (2026-10-30, owner: "a long undifferentiated stack"): Overview, DCF, Approval, Documents,
        Notes, Terms and Finance, History -- an accordion (components/board/RecordSection), so several stay open while an
        operator cross-checks; an empty one folds to a quiet line with its count. Folded content stays mounted.
      */}
      <RecordSections className="um-case-sections">
        <RecordSection id={sid('overview')} title="Overview" open={sectionOpen('overview')} onOpenChange={setSection('overview')}>
          <dl className="um-facts">
            <div className="um-fact"><dt>Stage</dt><dd>{stageLabelOf(c.stage, c.approval?.canonical === 'PROVEN')}</dd></div>
            <div className="um-fact"><dt>Submitted</dt><dd className={detail.submission.state === 'SUBMITTED' ? '' : 'none'}>
              {detail.submission.state === 'SUBMITTED'
                ? `${shortDate(detail.submission.sent_to_dcf_on || detail.submission.submitted_on)}${detail.submission.evidence_state === 'RECORDED_IN_EMERGE' ? ' · recorded here' : ''}`
                : detail.submission.state === 'NOT_SUBMITTED' ? 'Not submitted' : 'Not recorded'}
            </dd></div>
            <div className="um-fact"><dt>DCF account</dt><dd className={(account.need || account.state) === 'UNKNOWN' ? 'none' : account.need === 'NEEDED' || account.need === 'ISSUE' ? 'stop' : ''}>
              {account.need
                ? `${DCF_NEED_LABEL[account.need]}${account.need === 'PROVISIONED' && (account.email || account.existing?.email) ? ` · ${account.email || account.existing?.email}` : ''}`
                : detail.dcf_account ? DCF_NATIVE_LABEL[account.state] : DCF_ACCOUNT_LABEL[c.dcf_account_state]}
            </dd></div>
            <div className="um-fact"><dt>Last reviewed</dt><dd className={c.review?.last_reviewed_on || c.last_follow_up_on ? '' : 'none'}>
              {c.review?.last_reviewed_on ? `${shortDate(c.review.last_reviewed_on)}${c.review.last_reviewed_source === 'MONDAY' ? ' (board)' : ''}` : c.last_follow_up_on ? shortDate(c.last_follow_up_on) : 'Never'}
            </dd></div>
          </dl>

          {c.monday_differs && (
            <div className="um-section um-mondaydiff-panel">
              <MondayDiffers row={c} explain canMark={Boolean(writes) && canDo(scope, 'prepare_submission')} onDone={actDone} />
            </div>
          )}

          {/* The signed send-back to the originating PA: offered only when the server's scope says this caller may. */}
          {scope.can?.send_back && c.stage !== 'CLOSED' && (
            <div className="um-section">
              <SendBackAction
                path={sendBackPath.umed(c.id)} recordLabel={personName(c)} className="um-open" label="Send back to PA…"
                onClosed={(done) => { if (done) void onChanged(`Sent back to ${done.to.name}.`); }}
              />
            </div>
          )}

          {/* ATD: the panel fetches its own data and renders nothing for a caller without the ATD permissions. */}
          {c.patient_id && <AtdPanel patientId={c.patient_id} />}

          {/* Replacement: any office member may ask; the panel renders nothing where the server says the resident is out of reach. */}
          {c.patient_id && <ReplacementRequestPanel patientId={c.patient_id} residentName={personName(c)} onChanged={onChanged} />}

          {/* R7 (2026-10-28): a resubmission is never named on a case approved here or with a correction open (exceptionShown). */}
          {exceptionShown(c) && c.exception !== 'NONE' && (
            <div className="um-section">
              <h3>{EXCEPTION_LABEL[c.exception]}</h3>
              <p className="um-text">
                {c.exception === 'RESUBMISSION'
                  ? 'DCF has not shown any approval yet, so the same UMED goes to DCF again. Once a case is approved, months that do not reflect are a correction, not a resubmission.'
                  : c.exception === 'REVERSAL'
                  ? 'The claim is being unwound. The original UMED, its submission and anything Finance did downstream all still happened — this does not erase them.'
                  : c.exception === 'CROSSOVER'
                    ? 'NOCA came back but FLMMIS is not reflecting the reduced liability. One approval signal is not approval.'
                    : c.exception === 'CORRECTION'
                      ? 'A submitted UMED needs amending. This is a correction to an existing claim, not a new UMED.'
                      : 'The board marks this account a problem. What resolves that state is still an open business question (U-14).'}
              </p>
            </div>
          )}

          {c.lifecycle && (
            <div className="um-section"><h3>The resident</h3><p className="um-text">{LIFECYCLE_LABEL[c.lifecycle]}</p></div>
          )}

          {/* EP-010 §8: the VA's own act. Its own capability and its own write group. */}
          {writes && canDo(scope, 'confirm_enrollment') && <EnrollmentSection caseId={c.id} onDone={onChanged} />}
        </RecordSection>
        <RecordSection id={sid('dcf')} title="DCF" open={sectionOpen('dcf')} onOpenChange={setSection('dcf')}>
          {/* What is being sent and why, and whether eMerge can stand behind "ready" (packet §9–10). */}
          {readiness && (
            <div className="um-section" data-case-anchor="packet">
              <h3>Submission</h3>
              {(showReadiness || (detail.submissions && detail.submissions.length > 0)) && <dl className="um-kv">
                {showReadiness && (
                  <>
                    <dt>Sending</dt>
                    <dd>{CREATION_REASON_LABEL[readiness.purpose]}{readiness.purpose_from === 'CORRECTION' ? ' · for the open correction' : readiness.purpose_from === 'REVERSAL' ? ' · for the open reversal' : ''}</dd>
                    <dt>Readiness</dt>
                    <dd className={readiness.state === 'NOT_READY' ? 'stop' : ''}>
                      {readiness.state === 'READY' ? 'Ready' : readiness.state === 'REVIEW_REQUIRED' ? 'Review required' : readiness.state === 'NOT_READY' ? 'Not ready'
                        : readiness.state === 'CLOSED' ? (readiness.closed === 'CANCELLED' ? 'Closed: the corrected re-send was cancelled' : readiness.closed === 'WITHDRAWN' ? 'Closed: the corrected re-send was withdrawn' : 'Closed: the corrected packet reached DCF')
                          : 'Nothing proven missing; not verified'}
                    </dd>
                    {readiness.state !== 'CLOSED' && readiness.missing.length > 0 && <><dt>Missing</dt><dd>{readiness.missing.map((m) => REQUIREMENT_LABEL[m] || m).join(' · ')}</dd></>}
                    {readiness.untracked.length > 0 && <><dt>Not tracked yet</dt><dd className="um-quiet">{readiness.untracked.map((m) => REQUIREMENT_LABEL[m] || m).join(' · ')} — not proven present or absent in eMerge yet.</dd></>}
                  </>
                )}
                {detail.submissions && detail.submissions.length > 0 && (
                  <>
                    <dt>Sent</dt>
                    <dd>{detail.submissions.map((s) => `${shortDate(s.submitted_on)}${s.date_correction ? ` (corrected from ${shortDate(s.date_correction.from)}${s.date_correction.recorded_by_name ? ` by ${s.date_correction.recorded_by_name}` : ''})` : ''} · ${SUBMISSION_METHOD_LABEL[s.method]}${s.reference ? ` · ${s.reference}` : ''} (${s.episode_cause === 'WRONG_DOCUMENT_SENT' ? 'corrected re-send' : CREATION_REASON_LABEL[s.purpose]}${s.packet_version ? `, packet v${s.packet_version}` : ''}${s.source_system === 'MONDAY' ? ', from the board' : ''})`).join('; ')}</dd>
                  </>
                )}
              </dl>}
              {/* P0 2026-10-07: the packet eMerge prepared itself -- preview, download, confirm the upload; the DCF profile beside it. */}
              {!c.packet_preparation && c.forward_authority === 'EMERGE' && writes && canDo(scope, 'generate_packet') && c.stage !== 'CLOSED' && !c.submission_recorded && !plainSendingClosed && (
                <PreparePacketButton caseId={c.id} onChanged={onChanged} />
              )}
              {focus && focus.kind === 'choose-sending' && !(chosen && chosen.key === askKey) && c.packet_preparation && (
                <SendingChooser caseId={c.id} canSubmit={writes && canDo(scope, 'prepare_submission')}
                  onChoose={(kind) => { chosenSeq.current += 1; setChosen({ key: askKey, kind, n: chosenSeq.current }); }} />
              )}
              {/* UMED SUBMITTED TO DCF on a DCF resubmission whose packet is not prepared yet (Gwynn, 2026-10-15): say the next step. */}
              {focus && focus.kind === 'choose-sending' && !c.packet_preparation && (
                <p className="um-notice" role="status" data-testid="sending-needs-packet">
                  UMED SUBMITTED TO DCF: {c.creation_reason === 'DCF_RESUB' ? 'the packet for this resend to DCF' : 'the packet'} is not prepared yet.
                  {' '}Prepare it here and review it, then record how it was sent. Nothing is recorded until then.
                </p>
              )}
              {c.packet_preparation && (
                <PacketPreparationPanel
                  caseId={c.id}
                  canGenerate={writes && canDo(scope, 'generate_packet')}
                  canSubmit={writes && canDo(scope, 'prepare_submission')}
                  canView={detail.documents.available}
                  dcfNeed={c.dcf_account?.need || null}
                  dcfState={account.state || null}
                  dcfAccount={account}
                  submissionRecorded={Boolean(c.submission_recorded)}
                  onChanged={onChanged}
                  confirmRequest={(focus && focus.kind === 'confirm-upload' ? focus.n : 0) || (chosen && chosen.key === askKey && chosen.kind === 'confirm-upload' ? chosen.n : 0)}
                  sendRequest={focus && (focus.kind === 'fax-dcf' || focus.kind === 'record-fax') ? { kind: focus.kind, n: focus.n }
                    : (chosen && chosen.key === askKey && chosen.kind !== 'confirm-upload' ? { kind: chosen.kind, n: chosen.n } : null)}
                  canManageDcf={canDo(scope, 'manage_dcf_account')}
                  closed={c.stage === 'CLOSED'}
                  canSendBack={Boolean(scope.can?.send_back)}
                  canManageReversals={canDo(scope, 'manage_reversals')}
                  recordLabel={personName(c)}
                />
              )}
              {/* The packet, natively (D-XF-36): every version, and the form to make the next one. Generating never sends. */}
              {(detail.packets && detail.packets.length > 0) && (
                <ul className="um-packets" aria-label="Packet versions">{detail.packets.map((p) => <PacketRow key={`${p.id}:${p.sha256}`} caseId={c.id} packet={p}
                  canReview={!livePrep && Boolean(writes) && canDo(scope, 'prepare_submission') && detail.documents.available} onDone={onChanged} />)}</ul>
              )}
              {/* An approved case is not sent for approval again (final verification P2-B): the server refuses a plain sending on it. */}
              {plainSendingClosed && writes && (canDo(scope, 'generate_packet') || canDo(scope, 'prepare_submission')) && c.stage !== 'CLOSED' && !c.submission_recorded && <p className="um-quiet">Approved in eMerge: a new UMED is not sent for this case. A correction or a reversal is recorded from its own section.</p>}
              {/* The manual generator and sending form: only where no packet is being prepared automatically (one path per case, never two). */}
              {!livePrep && writes && canDo(scope, 'generate_packet') && c.stage !== 'CLOSED' && !c.submission_recorded && !plainSendingClosed && (
                <PacketForm caseId={c.id} packets={detail.packets || []} onDone={onChanged} />
              )}
              {!livePrep && writes && canDo(scope, 'prepare_submission') && c.stage !== 'CLOSED' && !c.submission_recorded && !plainSendingClosed && (c.creation_reason || c.stage === 'READY' || flowSending) && (
                <SubmissionForm caseId={c.id} scope={scope} packetId={sendablePacketId} onDone={onChanged} />
              )}
            </div>
          )}

          {/* Document correction (2026-10-14): a wrong document went to DCF; offered once it is marked, shown while the re-send is open. */}
          {/* PR #420 round 9: with no packet preparation (no panel above), a fax of the case that waits for a person is still shown, with its acts. */}
          {!c.packet_preparation && <CaseWaitingFaxes key={c.id} caseId={c.id} onChanged={onChanged} />}
          <CorrectedResend caseId={c.id} info={detail.corrected_resend} canOpen={Boolean(writes) && canDo(scope, 'prepare_submission')} onChanged={onChanged} />

          {(detail.dcf_account || c.dcf_account_state === 'REQUIRED' || canDo(scope, 'manage_dcf_account')) && (
            <DcfAccountPanel key={`${c.id}:${account.state}:${account.email || ''}:${account.version || 0}`} caseId={c.id} patientId={c.patient_id} account={account} scope={scope} onDone={onChanged} purpose={c.readiness?.purpose || c.creation_reason || null}
              benefits={c.dcf_benefits || null} closed={c.stage === 'CLOSED'} createRequest={focus && focus.kind === 'dcf-email' ? focus.n : 0} />
          )}

          {/* UM-R5: what DCF mailed this resident's DCF login. Loads its own rows; silent when there are none and the ingest is off. */}
          <DcfMailCaseSection caseId={c.id} onChanged={onChanged} />
        </RecordSection>
        <RecordSection id={sid('approval')} title="Approval" open={sectionOpen('approval')} onOpenChange={setSection('approval')}>
          <div data-case-anchor="approval">
          <ApprovalSection a={c.approval} visible={scope.approval_review} nocaEvidence={c.noca_evidence || null}
            act={writes && canDo(scope, 'review_approval') ? <div className="um-approval-act"><ApproveAct key={focus && focus.kind === 'approve' ? `approve-${focus.n}` : 'approve'}
              startConfirming={Boolean(focus && focus.kind === 'approve')} focusCommit={!(focus && focus.kind === 'approve')} row={c} onDone={(m) => { actDone(m); setEmailAsk((a) => ({ caseId: c.id, n: a.n + 1 })); }} /></div> : null} />
          </div>
          {/* SCRUM-132: who to bill and contact, during review and after (the server's approval gate decides). */}
          <BillingContactBlock contact={detail.billing_contact || null} />
          {approvedHere && scope.approval_review && (
            <ApprovalEmailPanel caseId={c.id} onChanged={onChanged}
              openRequest={(focus && focus.kind === 'approval-email' ? focus.n : 0) + (emailAsk.caseId === c.id ? emailAsk.n * 100000 : 0)} />
          )}

          {scope.approval_review && detail.reviews && detail.reviews.length > 0 && (
            <div className="um-section">
              <h3>Reviews</h3>
              <ol className="um-story">
                {detail.reviews.map((r) => (
                  <li key={r.id}>
                    <span className="when">{shortDate(r.reviewed_at)}</span>
                    <span className="what"><strong>NOCA {NOCA_RESULT_LABEL[r.noca_result]} · FLMMIS {FLMMIS_RESULT_LABEL[r.flmmis_result]}</strong><span> · {DECISION_LABEL[r.decision]}{r.reviewed_by_name ? ` · ${r.reviewed_by_name}` : ''}{r.note ? ` · ${r.note}` : ''}</span></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {writes && canDo(scope, 'review_approval') && c.stage !== 'CLOSED' && c.approval.canonical !== 'PROVEN' && (
            <div className="um-section" data-case-anchor="review">
              <h3>Record a review</h3>
              <ReviewForm key={`${c.id}:${c.approval.noca}`} caseId={c.id} onDone={onChanged} resubmission={c.resubmission || null} currentNoca={c.approval.noca} />
            </div>
          )}

          <CorrectionsPanel caseId={c.id} corrections={detail.corrections || []} scope={scope} onDone={onChanged}
            partialCorrectionId={c.partial_approval?.correction_open ? c.partial_approval.correction_id : null}
            plCorrectionId={c.after_approval?.pl_correction?.state === 'OPEN' ? c.after_approval.pl_correction.correction_id : null}
            resubmittalOpen={(detail.reversals || []).some((v) => v.state !== 'COMPLETED' && v.state !== 'WITHDRAWN')}
            openSent={!c.correction || c.correction.sent_since_opened !== false} row={c} />
          <ReversalsPanel caseId={c.id} reversals={detail.reversals || []} scope={scope} onDone={onChanged} approvedByAct={c.approval?.canonical === 'PROVEN'} />
        </RecordSection>
        {docsShown && (
          <RecordSection id={sid('documents')} title="Documents" open={sectionOpen('documents')} onOpenChange={setSection('documents')} count={docCount} countLabel="documents" empty={!docCount} summary={detail.documents.available ? (docCount ? null : 'None yet') : 'Not available'}>
            {/* The documents this case depends on, as eMerge holds them (workforce write cutover). */}
            {detail.documents.available && (
              <div className="um-section">
                {detail.documents.items.length === 0
                  ? <p className="um-quiet">eMerge holds no document for this resident.</p>
                  : <ul className="um-docs-list" aria-label="Case documents">{detail.documents.items.filter((d) => d.document_class !== 'UMED_GENERATED_PACKET').map((d) => <DocumentRow key={d.id} caseId={c.id} doc={d} canMark={Boolean(writes) && canDo(scope, 'add_document')} onChanged={onChanged} />)}</ul>}
                {writes && canDo(scope, 'add_document') && <UmedUploadForm caseId={c.id} documentCount={detail.documents.items.length} onDone={onChanged} />}
              </div>
            )}

            {!detail.documents.available && detail.documents.why && (
              <div className="um-section">
                <p className="um-quiet">{detail.documents.why}</p>
              </div>
            )}
          </RecordSection>
        )}
        {detail.notes && (
          <RecordSection id={sid('notes')} title="Notes" open={sectionOpen('notes')} onOpenChange={setSection('notes')} count={notesCount} countLabel="notes" empty={!notesCount} summary={notesCount ? null : 'None yet'}>
            {/* The operators' notes (cutover 2026-09-21): shown when the payload carries them; writable when the caller may. */}
            {detail.notes && <NotesSection caseId={c.id} notes={detail.notes} canWrite={Boolean(writes) && canDo(scope, 'add_note')} onDone={onChanged} />}
          </RecordSection>
        )}
        <RecordSection id={sid('terms')} title="Terms & Finance" open={sectionOpen('terms')} onOpenChange={setSection('terms')} empty={!termsHeld && !financeShown} summary={termsHeld || financeShown ? null : 'No terms recorded'}>
          <TermsPanel caseId={c.id} terms={detail.terms} canWrite={Boolean(writes) && canDo(scope, 'prepare_submission') && c.stage !== 'CLOSED'} onDone={onChanged} />
          {scope.finance_visible && c.finance && (
            <div className="um-section">
              <h3>Finance</h3>
              <p className="um-text">{HANDOFF_LABEL[c.finance.handoff]}</p>
              <p className="um-quiet">
                {c.finance.handoff === 'MISSING'
                  ? 'Approval is proven and no Finance account exists. Creating it is a later phase; this screen only shows the gap.'
                  : c.finance.handoff === 'NOT_ESTABLISHED'
                    ? 'No handoff follows until the case is approved: record NOCA received and FLMMIS reflected, then move it to Approved.'
                    : 'Whether a proven approval reached Finance. Aging, invoices and payments live in the Finance workspace, not here.'}
              </p>
            </div>
          )}
        </RecordSection>
        <RecordSection id={sid('history')} title="History" open={sectionOpen('history')} onOpenChange={setSection('history')} count={detail.events.length} countLabel="events" empty={detail.events.length === 0}>
          <div className="um-section">
            <h3>Story</h3>
            {detail.events.length === 0 ? <p className="um-quiet">Nothing recorded yet.</p> : (
              <ol className="um-story">
                {detail.events.map((e) => (
                  <li key={e.id}>
                    <span className="when">{shortDate(e.occurred_at)}</span>
                    <span className="what">
                      <strong>{e.event_type.toLowerCase().replace(/_/g, ' ')}</strong>
                      <span> · {e.source_system === 'MONDAY' ? 'from the board' : e.actor_name || 'eMerge'}</span>
                      {e.note ? <span className="um-quiet" data-testid="event-note"> · {e.note}</span> : null}
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {done.length > 0 && (
              <ul className="um-actions">
                {done.map((a) => (
                  <li key={a.id} className="done">
                    <span>{ACTION_LABEL[a.action_type]}</span>
                    <span className="due">{a.status === 'CANCELLED' ? 'no longer stated by the source' : 'done'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {scope.provenance_visible && detail.provenance && (
            <div className="um-section">
              <h3>Provenance</h3>
              <button type="button" className="um-link" aria-expanded={showProvenance} onClick={() => setShowProvenance((v) => !v)}>
                {showProvenance ? 'Hide' : 'Show'} where this came from
              </button>
              {showProvenance && (
                <dl className="um-kv">
                  <dt>Source</dt><dd>{detail.provenance.source_system} · {detail.provenance.source_record_type}</dd>
                  <dt>Rule</dt><dd>{detail.provenance.source_rule}</dd>
                  <dt>Vocabulary</dt><dd>v{detail.provenance.vocabulary_version} (provisional)</dd>
                  <dt>Board status</dt><dd>{c.source_status_raw || <NotRecorded />}</dd>
                  <dt>Board faxed column</dt><dd>{c.source_faxed_status_raw || <NotRecorded />}</dd>
                  <dt>Board terms</dt><dd>{detail.provenance.source_terms_raw || <NotRecorded />}</dd>
                  <dt>Projected</dt><dd>{shortDateTime(detail.provenance.projected_at)}</dd>
                </dl>
              )}
            </div>
          )}
        </RecordSection>
      </RecordSections>

      {writes ? (
        <div className="um-readonly">
          <strong>Recorded here:</strong> what this workspace is open for — the sending of a packet, the DCF account, approval reviews, corrections, reversals. The fax or upload itself, and anything at DCF or Finance, still happen outside eMerge; this screen records what was done and opens the next obligation.
        </div>
      ) : (
        <div className="um-readonly">
          <strong>Read-only.</strong> Recording a submission, a NOCA, a correction or a Finance handoff is not available yet. Those still happen in Monday and DCF; this screen reads them.
        </div>
      )}
    </section>
  );
}

/* ── Shell ─────────────────────────────────────────────────────────────── */

export default function UmedShell() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const view: View = isView(rawView) ? rawView : DEFAULT_VIEW;
  const caseId = searchParams.get('case');
  const q = searchParams.get('q') || '';
  // A retired key in an old link (needs-dcf-account) lands on the default, the same view the server answers with.
  const rawLens = searchParams.get('lens');
  const lens: LensKey = isLensKey(rawLens) ? rawLens : DEFAULT_LENS;
  /*
   * All cases is one search box and every case in scope (Josie Evans, 2026-10-06: remove the lens bubbles). It
   * takes no lens, so an old link carrying one still lands on every case rather than a hidden filter.
   */
  const allLens: LensKey | null = null;
  const kind = (searchParams.get('kind') || DEFAULT_KIND) as ExceptionKey;
  const whose: 'mine' | 'all' = searchParams.get('whose') === 'all' ? 'all' : 'mine';
  const pageNo = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
  const viewLens: LensKey | null = view === 'all' ? allLens : lens;
  // A tab from an old link that the server no longer offers lands on the
  // board's first view, never on "everything".
  const rawTab = searchParams.get('tab');
  const tab: BoardTabKey = isBoardTab(rawTab) ? rawTab : DEFAULT_BOARD_TAB;
  const layout: Layout = boardLayoutOf(searchParams);
  const kanban = view === 'board' && layout === 'kanban';
  const loc: Loc = { view, caseId, q, page: pageNo, lens: viewLens, kind, whose, tab, layout,
    kOwner: searchParams.get('owner'), kSort: searchParams.get('sort'), kDir: searchParams.get('dir') };
  const go = (patch: Partial<Loc>, replace = false) => navigate(path({ ...loc, ...patch }), { replace });

  const [scope, setScope] = useState<Scope | null>(null);
  const [scopeRefusal, setScopeRefusal] = useState<Refusal | null>(null);
  const [scopeLoading, setScopeLoading] = useState(true);

  const [work, setWork] = useState<WorkPage | null>(null);
  const [followUps, setFollowUps] = useState<FollowUpPage | null>(null);
  const [approved, setApproved] = useState<ApprovedPage | null>(null);
  const [all, setAll] = useState<Paged<UmedCase> | null>(null);
  const [exceptions, setExceptions] = useState<ExceptionPage | null>(null);
  const [viewRefusal, setViewRefusal] = useState<Refusal | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  /** Which list (view · lens · kind · search · page · whose) the loaded rows belong to — so a stale list is never read as the new lens. */
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // Work another person sent back to this operator (Finance returns a handoff, design §3.3). Shown only when there is some.
  const returned = useReturned();
  // "ATD — all residents" is RESIDENT_ATD_MARK's (verifier on #399): the door is drawn only for a caller who holds it.
  const atdAll = useAtdAllResidents(Boolean(scope));
  // Where the door was opened from (view, search, lens, page, case), so "Back" returns there rather than to a bare view.
  const atdBack = useRef<{ to: string; label: string } | null>(null);

  const [fbOpen, setFbOpen] = useState(false);
  const [fbSource, setFbSource] = useState<PromptSource>('DRAWER');
  const openFeedback = useCallback((s: PromptSource) => { setFbSource(s); setFbOpen(true); }, []);

  const loadScope = useCallback(async () => {
    setScopeLoading(true); setScopeRefusal(null);
    try {
      const res = await umedApi.scope();
      if (res.success && res.data) setScope(res.data);
      else setScopeRefusal(refusalFor(res.code));
    } catch { setScopeRefusal(OFFLINE); } finally { setScopeLoading(false); }
  }, []);
  useEffect(() => { loadScope(); }, [loadScope]);

  useEffect(() => {
    if (rawView !== null && !isView(rawView)) go({ view: DEFAULT_VIEW }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawView]);

  // Search as you type (round 4): each read is numbered, and only the newest may write the list.
  const viewSeq = useRef(0);
  const loadView = useCallback(async () => {
    viewSeq.current += 1;
    const mine = viewSeq.current;
    const current = () => viewSeq.current === mine;
    setViewLoading(true); setViewRefusal(null);
    const l = { q, page: pageNo };
    try {
      if (view === 'work') {
        const [w, e] = await Promise.all([umedApi.work({ ...l, lens }), umedApi.exceptions({ q, kind })]);
        if (!current()) return;
        if (w.success && w.data) setWork(w.data); else setViewRefusal(refusalFor(w.code));
        if (e.success && e.data) setExceptions(e.data);
      }
      if (view === 'follow-ups') { const r = await umedApi.followUps({ ...l, whose }); if (!current()) return; if (r.success && r.data) setFollowUps(r.data); else setViewRefusal(refusalFor(r.code)); }
      if (view === 'approved') { const r = await umedApi.approved(l); if (!current()) return; if (r.success && r.data) setApproved(r.data); else setViewRefusal(refusalFor(r.code)); }
      if (view === 'all') { const r = await umedApi.all({ ...l, lens: allLens }); if (!current()) return; if (r.success && r.data) setAll(r.data); else setViewRefusal(refusalFor(r.code)); }
      setLoadedKey(`${view}|${viewLens ?? ''}|${kind}|${q}|${pageNo}|${whose}`);
    } catch { if (current()) setViewRefusal(OFFLINE); } finally { if (current()) setViewLoading(false); }
  }, [view, q, pageNo, lens, allLens, viewLens, kind, whose]);
  useEffect(() => { setWork(null); setFollowUps(null); setApproved(null); setAll(null); }, [view]);
  useEffect(() => { if (scope) loadView(); }, [scope, loadView]);

  /**
   * Work crossed to this person: re-read the list, and the counts with it.
   *
   * The signal changes when a notification arrives, so the table catches up
   * because something happened — not because a timer fired. Skipped on the
   * first value, which is only the poll saying what was already there.
   */
  const handoff = useHandoffSignal();
  const lastHandoff = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastHandoff.current === undefined) { lastHandoff.current = handoff; return; }
    if (lastHandoff.current === handoff) return;
    lastHandoff.current = handoff;
    if (!scope) return;
    loadView();
    umedApi.scope().then((s) => { if (s.success && s.data) setScope(s.data); }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff]);

  /*
   * Only the newest read may write the panel, and only for the case it asked for (verification of the Kanban,
   * 2026-10-14: open A, then B; A's slower answer landed under ?case=B, and an act chosen for B -- Approve -- opened
   * on A). Each read is numbered; a change of case makes every read in flight stale.
   */
  const detailSeq = useRef(0);
  const loadDetail = useCallback(async (id: string) => {
    detailSeq.current += 1;
    const mine = detailSeq.current;
    const current = () => detailSeq.current === mine;
    setDetailLoading(true); setDetailRefusal(null);
    try {
      const r = await umedApi.case(id);
      if (!current()) return;
      if (r.success && r.data) { if (r.data.case && sameId(r.data.case.id, id)) setDetail(r.data); }
      else {
        setDetail(null);
        setDetailRefusal(r.code === 'NOT_FOUND'
          ? { title: 'No such case here', body: 'This case does not exist, or it belongs to a building outside your scope. Those are deliberately the same answer.' }
          : refusalFor(r.code));
      }
    } catch { if (current()) { setDetail(null); setDetailRefusal(OFFLINE); } } finally { if (current()) setDetailLoading(false); }
  }, []);
  useEffect(() => { detailSeq.current += 1; setDetail(null); setDetailRefusal(null); setNotice(null); }, [caseId]);
  /** The panel shows a case only when it IS the case in the URL; anything else is a read still on its way. */
  const shownDetail = detail && detail.case && sameId(detail.case.id, caseId) ? detail : null;
  // Where a board act continues in the case panel (Gwynn, 2026-10-12). It belongs to the case it was chosen on.
  const [focus, setFocus] = useState<{ caseId: string; focus: CaseFocus } | null>(null);
  const focusSeq = useRef(0);
  const askFocus = useCallback((id: string, kind: CaseFocus['kind']) => { focusSeq.current += 1; setFocus({ caseId: id, focus: { kind, n: focusSeq.current } }); }, []);
  const caseFocus = focus && sameId(focus.caseId, caseId) && shownDetail ? focus.focus : null;
  useEffect(() => { if (scope && caseId) loadDetail(caseId); }, [scope, caseId, loadDetail]);

  /**
   * No stale context across lenses (April, 2026-09-11; packet §25). The
   * selected case belongs to the list it was opened from. When the list
   * underneath changes — another lens, kind, tab, search or page — and the
   * loaded list does not contain the case, the selection clears. A list that
   * is merely re-read after an act (same list) keeps its case, so a notice
   * is never swallowed. A deep link adopts the first list it lands on.
   */
  const listKey = `${view}|${viewLens ?? ''}|${kind}|${q}|${pageNo}|${whose}`;
  const selectedFrom = useRef<string | null>(null);
  useEffect(() => { if (!caseId) selectedFrom.current = null; }, [caseId]);
  useEffect(() => {
    if (!caseId || viewLoading || loadedKey !== listKey) return;
    const items: Array<{ id: string }> | null = view === 'work'
      ? (work ? [...work.items, ...(exceptions?.items || [])] : null)
      : view === 'follow-ups' ? (followUps?.items || null)
        : view === 'approved' ? (approved?.items || null)
          : (all?.items || null);
    if (!items) return;
    if (selectedFrom.current === null || selectedFrom.current === listKey) { selectedFrom.current = listKey; return; }
    if (items.some((c) => c.id === caseId)) { selectedFrom.current = listKey; return; }
    go({ caseId: null }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, viewLoading, loadedKey, work, exceptions, followUps, approved, all, listKey]);

  /**
   * The board reads its own rows, so an act in the case panel under a row tells it to read them again (2026-10-12: after
   * Confirm DCF upload the row still said READY TO SUBMIT until a manual reload). A fresh number per act.
   */
  const [boardChange, setBoardChange] = useState<{ n: number; msg: string; caseId: string | null } | null>(null);
  /** After a native act: say what happened, then re-read the case, the list and the counts — the screen shows what was recorded, never what was assumed. */
  const onChanged = useCallback(async (msg: string) => {
    setNotice(msg);
    setBoardChange((b) => ({ n: (b ? b.n : 0) + 1, msg, caseId }));
    const [s] = await Promise.all([umedApi.scope(), caseId ? loadDetail(caseId) : Promise.resolve(), loadView(), returned.reload()]);
    if (s.success && s.data) setScope(s.data);
  }, [caseId, loadDetail, loadView, returned.reload]);

  const feedback = (
    <FeedbackDrawer open={fbOpen} onClose={() => setFbOpen(false)} promptSource={fbSource}
      workspace="UMED" routeTemplate={BASE} viewName={view} facilityId={detail?.case.facility_id ?? null} />
  );

  if (scopeLoading) return <div className="um"><main className="um-main one"><Skeleton /></main></div>;
  if (scopeRefusal) {
    return (
      <div className="um">
        <main className="um-main one">
          <div><StateBlock refusal={scopeRefusal} retry={loadScope} /><div className="um-door"><FeedbackButton onClick={() => openFeedback('DRAWER')} /></div></div>
        </main>
        {feedback}
      </div>
    );
  }
  if (!scope) return null;

  const counts = scope.counts;
  const open = (id: string) => { selectedFrom.current = listKey; go({ caseId: id }); };
  const onPage = (p: number) => go({ page: p });
  // Board loads its own rows (it is the only view with its own sort state), so it is never "waiting" here.
  const hasData = view === 'board' || view === 'dcf-mail' || view === 'atd' || view === 'resubmittals' || view === 'replacements' ? true
    : view === 'work' ? Boolean(work) : view === 'follow-ups' ? Boolean(followUps) : view === 'approved' ? Boolean(approved) : Boolean(all);
  const visibleViews = VIEWS.filter((v) => v !== 'atd')
    .filter((v) => v !== 'approved' || scope.approval_review)
    .filter((v) => v !== 'follow-ups' || scope.approval_review);
  // DCF mail counts its own queue (the tab shows it); the bar carries no number for it.
  const tabCount = (v: View): number | null => (
    v === 'replacements' ? (scope.replacements ? scope.replacements.open : null)
      : v === 'dcf-mail' || v === 'atd' || v === 'resubmittals' ? null
      : v === 'work' ? counts.my_work
      : v === 'follow-ups' ? (counts.follow_ups || counts.follow_ups_all)
        : v === 'approved' ? counts.approved
          : counts.all_cases
  );
  /*
   * The sidebar (2026-10-14) in place of the tab bar: the same views, the same links (path(), so every deep link still
   * lands), the same counts as badges. Board became its two layouts under Views -- Kanban (the default) and Table --
   * first, where Board's tab was (Isaiah, 2026-09-22). The workspace opens on the Kanban (DEFAULT_VIEW, Isaiah Cotto
   * 2026-10-14); My work is ?view=work.
   */
  const viewHref = (v: View, patch: Partial<Loc> = {}) => path({ ...loc, view: v, page: 1, caseId: null, ...(view === 'atd' || view === 'replacements' ? { q: '' } : {}), ...patch });
  const ICON: Record<Exclude<View, 'board' | 'atd'>, typeof Inbox> = { work: Inbox, 'follow-ups': CalendarClock, approved: BadgeCheck, all: Files, 'dcf-mail': Mail, resubmittals: RotateCcw, replacements: Ear };
  const boardCount = tabCount('board');
  // Returned to you (verification I2, 2026-10-14): listed on My work, said on the board in one line, never a tile.
  const returnedN = returnedCount(returned.state) || 0;
  const sidebarGroups: SidebarGroup[] = [
    {
      key: 'views', label: 'Views',
      items: visibleViews.includes('board') ? [
        { key: 'kanban', label: 'Kanban', icon: SquareKanban, href: viewHref('board', { layout: 'kanban' }), active: view === 'board' && layout === 'kanban',
          badge: boardCount !== null ? { value: boardCount, label: `${boardCount} cases in your scope` } : null,
          title: 'The board as lanes: every open case, placed by the server. A drag never records anything.' },
        { key: 'table', label: 'Table', icon: Table2, href: viewHref('board', { layout: 'table' }), active: view === 'board' && layout === 'table',
          title: 'The board as one wide table, by the tabs it is reconciled across.' },
      ] : [],
    },
    {
      key: 'work', label: 'Work',
      items: visibleViews.filter((v): v is Exclude<View, 'board' | 'atd'> => v !== 'board').map((v) => {
        const n = tabCount(v);
        return { key: v, label: VIEW_LABEL[v], icon: ICON[v], href: viewHref(v), active: view === v, badge: n !== null ? { value: n } : null,
          // Work sent back to this operator is listed on My work; the workspace opens on the board, so the sidebar says so.
          secondary: v === 'work' && returnedN ? { value: returnedN, label: `${returnedN} returned to you`, tone: 'stop' as const } : null };
      }),
    },
  ];

  return (
    <div className={`um${view === 'atd' || view === 'replacements' ? ' board-host' : ''}`}>
      <WorkspaceMenu current="UMED" />
      <WorkspaceLayout workspace="UMED" navLabel="UMED views" groups={sidebarGroups}>
      <header className="um-bar">
        <div className="um-bar-inner">
          <div className="um-ident">
            <div className="wsb-title">
            <SidebarTrigger />
            <div className="um-where">
              <span className="um-eyebrow">eMerge · UMED operations</span>
              <h1>{titleOf(view, layout)}</h1>
              <span className="um-sub" title={mondayRetiredLine(counts) || undefined}>
                {updatedStamp(counts)}
                {scope.enterprise ? '' : ` · ${scope.facility_count} ${scope.facility_count === 1 ? 'building' : 'buildings'}`}
              </span>
            </div>
            </div>
            <div className="um-aside">
              {/* Every current resident, case or not (Josie, 2026-10-13: "Every active resident should reflect there"). */}
              {view !== 'atd' && atdAll && (
                <Link to={path({ view: 'atd' })} className="um-open" onClick={() => { atdBack.current = { to: path(loc), label: titleOf(view, layout) }; }}
                  title="Open every current resident, with or without a UMED case or a Finance account, to mark ATD.">Mark ATD for any resident</Link>
              )}
              {view === 'atd' && (
                <Link to={atdBack.current ? atdBack.current.to : path({ view: DEFAULT_VIEW })} className="um-open">
                  Back to {atdBack.current ? atdBack.current.label : titleOf(DEFAULT_VIEW, DEFAULT_LAYOUT)}
                </Link>
              )}
              <FindResidentEntry />
              <FeedbackButton onClick={() => openFeedback('DRAWER')} />
            </div>
          </div>
        </div>
      </header>

      {view === 'atd' ? (
        <main className="um-main one board-main">
          <AtdResidentsBoard idBase="um-atd-all" from="UMED" />
        </main>
      ) : view === 'replacements' ? (
        <main className="um-main one wide board-main">
          {/* Its own search is bound to ?q=, so the shell's search box is not drawn. */}
          <UmedReplacementsBoard idBase="um-replacements" onChanged={() => { umedApi.scope().then((s) => { if (s.success && s.data) setScope(s.data); }).catch(() => undefined); }} />
        </main>
      ) : (
      <main className={`um-main${view === 'board' || view === 'resubmittals' ? ' one wide' : ''}`}>
        <div className="um-list">
          {view === 'board' && returnedN > 0 && (
            <p className="um-note um-returned-note" role="status">
              <span><strong>{returnedN === 1 ? '1 record was' : `${returnedN} records were`} returned to you.</strong>{' '}
                <Link to={path({ ...loc, view: 'work', page: 1, caseId: null })}>Open My work</Link> to see why and work {returnedN === 1 ? 'it' : 'them'}.</span>
            </p>
          )}
          {/* The Board layout carries its own search in its toolbar (the same ?q=). */}
          {!kanban && <SearchBar value={q} onSearch={(next, live) => go({ q: next, page: 1 }, Boolean(live))}
            placeholder={view === 'all' ? 'Type any name or case reference' : undefined} />}
          {view === 'work' && <ExportButton what="my cases" run={() => umedApi.exportWork({ q, lens })} />}
          {view === 'all' && <ExportButton what="all cases" run={() => umedApi.exportAll({ q, lens: allLens })} />}
          {view === 'work' && <LensBar scope={scope} lens={lens} onLens={(l) => go({ lens: l, page: 1 })} />}
          <FreshnessNote counts={counts} />
          {view === 'work' && returned.state.status === 'ready' && returned.state.rows.length > 0 && <ReturnedToYou state={returned.state} />}
          {counts.facility_unresolved > 0 && !scope.enterprise && (
            <div className="um-note">
              <span><strong>{counts.facility_unresolved} cases could not be placed in a building.</strong> Your scope is by building, so they are not in these lists. Counted here rather than hidden.</span>
            </div>
          )}
          {viewRefusal && <StateBlock refusal={viewRefusal} retry={loadView} />}
          {!viewRefusal && viewLoading && !hasData && <Skeleton />}
          {!viewRefusal && view === 'work' && work && (
            <>
              <WorkList data={work} scope={scope} lens={lens} selected={caseId} onOpen={open} onPage={onPage} />
              <ExceptionsPanel scope={scope} kind={kind} data={exceptions} selected={caseId}
                onKind={(k) => go({ kind: k, page: 1 })} onOpen={open} onPage={onPage} />
            </>
          )}
          {!viewRefusal && view === 'follow-ups' && followUps && (
            <FollowUpList data={followUps} counts={counts} whose={whose} onWhose={(w) => go({ whose: w, page: 1 })} selected={caseId} onOpen={open} onPage={onPage} />
          )}
          {!viewRefusal && view === 'approved' && approved && (
            <ApprovedList data={approved} financeVisible={scope.finance_visible} selected={caseId} onOpen={open} onPage={onPage}
              onConflicts={() => go({ view: 'work', kind: 'approval-conflict', page: 1 })} />
          )}
          {!viewRefusal && view === 'all' && all && <AllList data={all} selected={caseId} onOpen={open} onPage={onPage} />}
          {view === 'resubmittals' && <UmedResubmittalsBoard />}
          {view === 'dcf-mail' && <DcfMailView q={q} page={pageNo} selected={caseId} onOpenCase={open} onPage={onPage} />}
          {kanban && (
            <UmedKanban scope={scope} q={q} owner={loc.kOwner || null} sort={loc.kSort || null} dir={loc.kDir || null}
              onQuery={(p) => go({ ...('q' in p ? { q: p.q || '' } : {}), ...('owner' in p ? { kOwner: p.owner } : {}),
                ...('sort' in p ? { kSort: p.sort, kDir: p.dir || null } : {}), ...('dir' in p && !('sort' in p) ? { kDir: p.dir } : {}) }, true)}
              selected={caseId} onSelect={(id) => go({ caseId: id })} changed={boardChange}
              onContinue={(id, kind) => { askFocus(id, kind); if (caseId !== id) go({ caseId: id }); }}
              elsewhereLink={(e) => {
                // Approved in Monday only (2026-10-21): no card and no status menu here, and the reversal / resubmittal tile
                // lives on the Table's Main table row, so the chip opens that tab, carrying the search.
                const to = e.exception_kind === 'approval-conflict' && !e.tab ? path({ view: 'board', tab: 'main-table', layout: 'table', q })
                  : e.tab && isBoardTab(e.tab) ? path({ view: 'board', tab: e.tab, layout: 'table' })
                  : e.exception_kind ? path({ view: 'work', kind: e.exception_kind as ExceptionKey }) : null;
                return to ? { href: to, go: () => navigate(to) } : null;
              }}
              detail={<CaseContext detail={shownDetail} refusal={detailRefusal} loading={detailLoading || Boolean(caseId && detail && !shownDetail)} scope={scope} notice={notice} onChanged={onChanged} focus={caseFocus}
                statusActs onFocus={caseId ? (k) => askFocus(caseId, k) : undefined} />} />
          )}
          {view === 'board' && !kanban && (
            <BoardTable scope={scope} tab={tab} q={q} page={pageNo} selected={caseId} changed={boardChange}
              onClearSearch={() => go({ q: '', page: 1, caseId: null })}
              onTab={(t) => go({ tab: t, page: 1, caseId: null })} onPage={onPage}
              // Clicking the open row closes it, so the table comes back without a trip to the tab bar.
              onSelect={(id) => go({ caseId: caseId === id ? null : id })}
              // An act chosen on a row that continues in the case panel (UMED UPLOADED TO DCF, DCF EMAIL CREATED): open it there.
              onContinue={(id, kind) => { askFocus(id, kind); if (caseId !== id) go({ caseId: id }); }}
              detail={<CaseContext detail={shownDetail} refusal={detailRefusal} loading={detailLoading || Boolean(caseId && detail && !shownDetail)} scope={scope} notice={notice} onChanged={onChanged} focus={caseFocus} />} />
          )}
          <WorkflowPulse storageKey={`fb-pulse:UMED:${view}`} onFriction={() => openFeedback('WORKFLOW_PROMPT')} />
        </div>
        {view !== 'board' && view !== 'resubmittals' && (
          <aside className="um-context">
            <CaseContext detail={shownDetail} refusal={detailRefusal} loading={detailLoading || Boolean(caseId && detail && !shownDetail)} scope={scope} notice={notice} onChanged={onChanged} focus={caseFocus}
              statusActs onFocus={caseId ? (k) => askFocus(caseId, k) : undefined} />
          </aside>
        )}
      </main>
      )}
      </WorkspaceLayout>
      {feedback}
    </div>
  );
}
